import React, { useState, useEffect, useRef, useCallback } from 'react';
import { bearerHeaders } from './api.ts';
import { Mark, MarkAd, MarkApplication, MarkRequirements, DispensationRequest, ApplicationContract, QidUser, resolveUrl } from './api.ts';
import { MarkAdsPointer } from './views/jakt/MarkAdsPointer';
import { getTheme, useThemeMode } from './theme';
import { printHtml } from './nativePrint';
import { ContractPayButton } from './components/ContractPayButton';

const H = () => ({
  'Content-Type': 'application/json',
  ...bearerHeaders(localStorage.getItem('qvenari_access_token') || ''),
  'X-Api-Key': 'dev-secret',
});

async function gql(query: string, variables?: Record<string, unknown>) {
  const r = await fetch(resolveUrl('/graphql'), { method: 'POST', headers: H(), body: JSON.stringify({ query, variables }) });
  const ct = r.headers.get('content-type') || '';
  const raw = await r.text();
  if (!ct.includes('application/json')) {
    throw new Error(
      r.ok
        ? `Servern svarade med HTML i stället för JSON (${r.status}). Ladda om sidan eller kontakta support.`
        : `API-fel ${r.status}: förväntade JSON men fick ${ct.slice(0, 40) || 'okänt format'}.`,
    );
  }
  let d: { data?: unknown; errors?: { message: string }[] };
  try {
    d = JSON.parse(raw) as { data?: unknown; errors?: { message: string }[] };
  } catch {
    throw new Error('Ogiltigt svar från servern (inte JSON). Kontrollera nätverk och att api.qvenari.com nås.');
  }
  if (d.errors) throw new Error(d.errors[0].message);
  return d.data as any;
}

function statusLabel(s: string) {
  const map: Record<string, { label: string; color: string }> = {
    'interest_registered': { label: 'Intresse registrerat', color: '#EE8F09' },
    'pending': { label: 'Väntande', color: '#EE8F09' },
    'approved': { label: 'Godkänd', color: '#61D836' },
    'rejected': { label: 'Avvisad', color: '#c0392b' },
    'dispensation_requested': { label: 'Dispens begärd', color: '#9b59b6' },
    'dispensation_granted': { label: 'Dispens beviljad', color: '#61D836' },
    'dispensation_denied': { label: 'Dispens nekad', color: '#c0392b' },
    'question_asked': { label: 'Fråga från admin', color: '#00A2FF' },
    'contract_sent': { label: 'Signera avtal', color: '#00A2FF' },
    'user_signed': { label: 'Väntar på markägaren', color: '#9b59b6' },
    'contract_signed': { label: 'Avtal signerat', color: '#61D836' },
    'qualified': { label: 'Kvalificerad', color: '#61D836' },
    'completed': { label: 'Klart - Medlem', color: '#61D836' },
  };
  return map[s] || { label: s, color: '#6a6e6a' };
}

const INTEREST_ROLES = [
  { id: 'gastjagare', label: 'Gästjägare', desc: 'Delta på enstaka jakter som inbjuden gäst' },
  { id: 'medlem', label: 'Medlem', desc: 'Bli fast medlem i jaktlaget med full tillgång' },
  { id: 'hundforare', label: 'Hundförare', desc: 'Delta med jakthund på drev och spårning' },
  { id: 'eftersoksjagare', label: 'Eftersöksjägare', desc: 'Specialiserad på att spåra skadskjutet vilt' },
  { id: 'hjalpare', label: 'Hjälpreda', desc: 'Assistera vid jakt utan egen jakt-roll' },
];

// ── INTEREST MODAL ──────────────────────────────────────────────
// ── PROFILE REQUIREMENT CHECK ──────────────────────────────────
interface ProfileCheck {
  meetsAll: boolean;
  checks: Array<{ label: string; met: boolean; detail?: string }>;
}

function checkProfileAgainstRequirements(profile: any, requirements: MarkRequirements | null): ProfileCheck {
  if (!requirements) return { meetsAll: true, checks: [] };
  const checks: Array<{ label: string; met: boolean; detail?: string }> = [];

  if (requirements.minExperienceYears > 0) {
    const hunterSince = profile?.hunterSinceYear ? new Date().getFullYear() - parseInt(profile.hunterSinceYear) : 0;
    const met = hunterSince >= requirements.minExperienceYears;
    checks.push({ label: `Minst ${requirements.minExperienceYears} ars jakterfarenhet`, met, detail: met ? `${hunterSince} ar` : profile?.hunterSinceYear ? `Bara ${hunterSince} ar` : 'Ej angivet' });
  }

  if (requirements.requiresWeaponLicense) {
    const hasLicense = profile?.licenseUrls?.length > 0 || profile?.weapons?.length > 0;
    checks.push({ label: 'Vapenlicens kravs', met: !!hasLicense, detail: hasLicense ? 'Finns' : 'Saknas' });
  }

  if (requirements.requiresDog) {
    const hasDog = profile?.dogs?.length > 0;
    checks.push({ label: 'Jakthund kravs', met: !!hasDog, detail: hasDog ? `${profile.dogs.length} hund(ar)` : 'Saknas' });
  }

  if (requirements.afterhuntRequired) {
    const has = !!profile?.afterhuntCertified;
    checks.push({ label: 'Eftersoksutbildning kravs', met: has, detail: has ? 'Certifierad' : 'Saknas' });
  }

  if (requirements.customRequirements) {
    for (const cr of requirements.customRequirements.filter(r => r.required)) {
      checks.push({ label: cr.label, met: false, detail: 'Verifieras manuellt' });
    }
  }

  return { meetsAll: checks.every(c => c.met), checks };
}

// ── CONTRACT BUILDER ───────────────────────────────────────────
interface ContractConfig {
  // 1) Kontraktsperiod
  startDate: string;
  endDate: string;
  // 2) Forlangning & uppsagningstid
  autoRenewal: boolean;
  noticePeriodMonths: number;
  // 3) Jakt pa eget initiativ
  huntInitiative: 'calendar_approval' | 'free_available' | 'organized_only';
  // 4) Fallavgifter & kott
  useCostPlan: boolean;
  meatSharingOwnHunt: 'standard' | 'custom';
  meatSharingOrganized: 'standard' | 'custom';
  customMeatOwnHunt: string;
  customMeatOrganized: string;
}

function defaultContractConfig(): ContractConfig {
  const now = new Date();
  const year = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    startDate: `${year}-07-01`,
    endDate: `${year + 1}-06-30`,
    autoRenewal: true,
    noticePeriodMonths: 6,
    huntInitiative: 'calendar_approval',
    useCostPlan: true,
    meatSharingOwnHunt: 'standard',
    meatSharingOrganized: 'standard',
    customMeatOwnHunt: '',
    customMeatOrganized: '',
  };
}

function generateContractText(cfg: ContractConfig, hasCostPlan: boolean): string {
  const lines: string[] = [];

  lines.push('JAKTRÄTTSAVTAL');
  lines.push('');

  // 1) Kontraktsperiod
  lines.push('1. AVTALSTID');
  lines.push(`Avtalet gäller från ${cfg.startDate} till och med ${cfg.endDate}.`);
  lines.push('');

  // 2) Förlängning & uppsägningstid
  lines.push('2. FÖRLÄNGNING OCH UPPSÄGNINGSTID');
  if (cfg.autoRenewal) {
    lines.push(`Avtalet förlängs automatiskt med samma period om det inte sägs upp senast ${cfg.noticePeriodMonths} månader före ny periods början.`);
  } else {
    lines.push('Avtalet förlängs inte automatiskt. Nytt avtal måste tecknas för fortsatt medlemskap.');
  }
  lines.push('');

  // 3) Jakt på eget initiativ
  lines.push('3. JAKT PÅ EGET INITIATIV');
  if (cfg.huntInitiative === 'calendar_approval') {
    lines.push('Jakt får bedrivas efter bokning i kalendern och godkännande av jaktledaren. Alla jakter ska i förväg meddelas och godkännas av ansvarig jaktledare. Jakten ska startas via marknadsplatsen Qvenari omedelbart innan jaktens påbörjan, varvid jakten automatiskt dokumenteras med tid, plats och deltagare i systemet. Dokumentationsskyldigheten gäller oavsett jaktens längd eller omfattning.');
  } else if (cfg.huntInitiative === 'free_available') {
    lines.push('Jakträttstagaren har rätt att jaga fritt på tillgängliga datum. Jakten ska registreras i kalendern före jaktens start. Jakten ska startas via marknadsplatsen Qvenari omedelbart innan jaktens påbörjan, varvid jakten automatiskt dokumenteras med tid, plats och deltagare i systemet. Dokumentationsskyldigheten gäller oavsett jaktens längd eller omfattning.');
  } else {
    lines.push('Jakträttstagaren får endast delta på organiserade jakter som skapats av jaktledaren. Egen jakt på marken är inte tillåten.');
  }
  lines.push('');

  // 4) Fällavgifter och kött
  lines.push('4. FÄLLAVGIFTER OCH KÖTTFÖRDELNING');
  if (cfg.useCostPlan && hasCostPlan) {
    lines.push('Fällavgifter debiteras enligt markens kostnadsplan. Köttkostnad och köttfördelning sker via köttfördelningslistan.');
  } else {
    lines.push('Inga separata fällavgifter utgår. Köttfördelning sker via köttfördelningslistan.');
  }
  if (cfg.meatSharingOwnHunt === 'custom' && cfg.customMeatOwnHunt) {
    lines.push(`Vid egen jakt: ${cfg.customMeatOwnHunt}`);
  } else {
    lines.push('Vid egen jakt fördelas köttet enligt köttfördelningslistan.');
  }
  if (cfg.meatSharingOrganized === 'custom' && cfg.customMeatOrganized) {
    lines.push(`Vid organiserad jakt: ${cfg.customMeatOrganized}`);
  } else {
    lines.push('Vid organiserad jakt fördelas köttet enligt köttfördelningslistan.');
  }
  lines.push('');

  // 5) Jaktledare vid egen jakt
  lines.push('5. JAKTLEDARE VID EGEN JAKT');
  lines.push('Vid jakt på eget initiativ är jakträttstagaren själv jaktledare och ansvarar för att all jaktlagstiftning följs, inklusive att ha eftersöksjägare tillgängliga. Jakträttstagaren ansvarar även för korrekt rapportering av fällt vilt. Varje jakt på eget initiativ ska startas via marknadsplatsen Qvenari före jaktens påbörjan. Genom att starta jakten i systemet skapas automatisk dokumentation av jaktdatum, tid, plats, deltagare och eventuellt fällt vilt, vilket utgör underlag för avskjutningsrapportering och uppfyllande av viltvårdsansvar. Underlåtenhet att starta jakten via Qvenari betraktas som brott mot dokumentationsskyldigheten och kan utgöra grund för åtgärder enligt punkt 8.');
  lines.push('');

  // 6) Organiserad jakt
  lines.push('6. ORGANISERAD JAKT');
  lines.push('Vid organiserad jakt måste deltagarna underställa sig jaktledarens beslut avseende placering, skjutordning och övriga instruktioner. Varje jägare har dock eget ansvar för att säkerställa sin egen och andras säkerhet, inklusive korrekt vapenhantering och säker skjutning.');
  lines.push('');

  // 7) Etik och moral
  lines.push('7. ETIK OCH MORAL');
  lines.push('Jakträttstagaren ska alltid säkerställa god etik och moral vid jakt, både vad det gäller viltet, övriga jägare och omgivningen. Sexism, rasism och dåligt uppförande är grund för omedelbar uteslutning ur jaktlaget. Jakträttstagaren förväntas visa respekt för naturen, djuren och övriga människor under alla former av jakt.');
  lines.push('');

  // 8) Överträdelser
  lines.push('8. ÖVERTRÄDELSER');
  lines.push('Vid överträdelser mot detta avtal äger jaktledaren rätten att förverka jakträttstagarens rätt till jakt på marken för en period om högst resterande jaktåret. Beslut om avstängning meddelas skriftligen till jakträttstagaren. Grövre överträdelser kan leda till permanent uteslutning genom styrelsebeslut.');
  lines.push('');

  // 9) Vapen och legitimation
  lines.push('9. VAPEN OCH LEGITIMATION');
  lines.push('Vid jakt ska alltid giltiga vapenlicenser, statligt jaktkort och legitimation finnas tillgängliga. Vapnen ska vara kontrollerade och inskjutna före varje jaktsäsong. Jakträttstagaren ansvarar personligen för att dessa krav uppfylls vid varje jakttillfälle.');

  return lines.join('\n');
}

function ContractBuilder({ markId, onGenerate, onCancel }: {
  markId: string;
  onGenerate: (text: string) => void;
  onCancel: () => void;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [cfg, setCfg] = useState<ContractConfig>(defaultContractConfig());
  const [hasCostPlan, setHasCostPlan] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    gql(`query CM($id: ID!) { markCostModel(markId: $id) { arsavgift fallavgifter { viltslag kostnad } kottkostnad { viltslag kostnadPerKg } } }`, { id: markId })
      .then(d => { if (d.markCostModel && (d.markCostModel.fallavgifter?.length > 0 || d.markCostModel.arsavgift > 0)) setHasCostPlan(true); })
      .catch(() => {});
  }, [markId]);

  const u = (key: keyof ContractConfig, val: any) => setCfg(prev => ({ ...prev, [key]: val }));
  const contractText = generateContractText(cfg, hasCostPlan);

  const lS: React.CSSProperties = { color: T.textMuted, fontSize: 11, fontWeight: 700, marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' };
  const iS: React.CSSProperties = { width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '8px 12px', color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const secS: React.CSSProperties = { marginBottom: 16, padding: 12, background: `${T.surface}88`, borderRadius: 6, border: `1px solid ${T.border}` };
  const secTitle: React.CSSProperties = { color: T.amber, fontSize: 12, fontWeight: 700, marginBottom: 8 };

  if (preview) {
    return (
      <div>
        <div style={{ color: T.amber, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Förhandsgranska kontrakt</div>
        <div style={{ maxHeight: 500, overflowY: 'auto', marginBottom: 16, borderRadius: 4 }}>
          <LegalContractDocument
            contractText={contractText}
            markName="[Jaktmarkens namn]"
            contractRef="QV-MALL"
            showSignaturePlaceholder={true}
          />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setPreview(false)} style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '10px 16px', cursor: 'pointer', flex: 1 }}>Tillbaka</button>
          <button onClick={() => onGenerate(contractText)} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 4, padding: '10px 16px', fontWeight: 700, cursor: 'pointer', flex: 2 }}>Använd detta kontrakt</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ color: T.amber, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Skapa kontrakt</div>

      {/* 1. Kontraktsperiod */}
      <div style={secS}>
        <div style={secTitle}>1. Kontraktsperiod</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={lS}>Startdatum</label>
            <input type="date" value={cfg.startDate} onChange={e => u('startDate', e.target.value)} style={iS} />
          </div>
          <div>
            <label style={lS}>Slutdatum</label>
            <input type="date" value={cfg.endDate} onChange={e => u('endDate', e.target.value)} style={iS} />
          </div>
        </div>
      </div>

      {/* 2. Forlangning & uppsagningstid */}
      <div style={secS}>
        <div style={secTitle}>2. Forlangning och uppsagningstid</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.text, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={cfg.autoRenewal} onChange={e => u('autoRenewal', e.target.checked)} />
          Automatisk forlangning
        </label>
        {cfg.autoRenewal && (
          <div>
            <label style={lS}>Uppsagningstid (manader fore ny period)</label>
            <select value={cfg.noticePeriodMonths} onChange={e => u('noticePeriodMonths', Number(e.target.value))} style={iS}>
              {[1, 2, 3, 4, 5, 6, 9, 12].map(m => <option key={m} value={m}>{m} manader</option>)}
            </select>
          </div>
        )}
      </div>

      {/* 3. Jakt på eget initiativ */}
      <div style={secS}>
        <div style={secTitle}>3. Jakt på eget initiativ</div>
        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 10, lineHeight: 1.4 }}>
          OBS: Vid jakt på eget initiativ (kalenderbokning eller fritt) gäller alltid dokumentationsskyldighet — jakten ska startas via Qvenari innan jakten påbörjas.
        </div>
        {([
          ['calendar_approval', 'Bokad i kalendern och godkänns av jaktledaren'] as const,
          ['free_available', 'Fritt vid tillgängliga datum'] as const,
          ['organized_only', 'Endast delta på skapade/organiserade jakter'] as const,
        ]).map(([val, label]) => (
          <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.text, fontSize: 13, marginBottom: 6, cursor: 'pointer' }}>
            <input type="radio" name="huntInit" checked={cfg.huntInitiative === val} onChange={() => u('huntInitiative', val)} />
            {label}
          </label>
        ))}
      </div>

      {/* 4. Fallavgifter och kott */}
      <div style={secS}>
        <div style={secTitle}>4. Fallavgifter och kott</div>
        {hasCostPlan ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.text, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={cfg.useCostPlan} onChange={e => u('useCostPlan', e.target.checked)} />
            Anvand markens kostnadsplan for fallavgifter och kottkostnad
          </label>
        ) : (
          <div style={{ color: T.textMuted, fontSize: 12, marginBottom: 8 }}>Ingen kostnadsplan definierad — inga fallavgifter eller kottkostnad inkluderas.</div>
        )}

        <div style={{ marginTop: 8 }}>
          <label style={lS}>Kottfordelning vid egen jakt</label>
          <select value={cfg.meatSharingOwnHunt} onChange={e => u('meatSharingOwnHunt', e.target.value)} style={{ ...iS, marginBottom: 6 }}>
            <option value="standard">Enligt kottfordelningslistan</option>
            <option value="custom">Anpassad</option>
          </select>
          {cfg.meatSharingOwnHunt === 'custom' && (
            <input value={cfg.customMeatOwnHunt} onChange={e => u('customMeatOwnHunt', e.target.value)} placeholder="Beskriv fordelning..." style={{ ...iS, marginBottom: 4 }} />
          )}
        </div>
        <div style={{ marginTop: 6 }}>
          <label style={lS}>Kottfordelning vid organiserad jakt</label>
          <select value={cfg.meatSharingOrganized} onChange={e => u('meatSharingOrganized', e.target.value)} style={{ ...iS, marginBottom: 6 }}>
            <option value="standard">Enligt kottfordelningslistan</option>
            <option value="custom">Anpassad</option>
          </select>
          {cfg.meatSharingOrganized === 'custom' && (
            <input value={cfg.customMeatOrganized} onChange={e => u('customMeatOrganized', e.target.value)} placeholder="Beskriv fordelning..." style={{ ...iS, marginBottom: 4 }} />
          )}
        </div>
      </div>

      {/* 5-9 visas som sammanfattning */}
      <div style={{ ...secS, background: `${T.amber}08` }}>
        <div style={secTitle}>5-9. Standardvillkor (ingår automatiskt)</div>
        <div style={{ color: T.textMuted, fontSize: 12, lineHeight: 1.7 }}>
          <div style={{ marginBottom: 4 }}>5. Jaktledare vid egen jakt — jagaren ar sjalv jaktledare och ansvarar for lagefterlevnad inkl eftersok</div>
          <div style={{ marginBottom: 4 }}>6. Organiserad jakt — jagare understaller sig jaktledarens beslut, eget ansvar for sakerhet</div>
          <div style={{ marginBottom: 4 }}>7. Etik och moral — god etik, respekt for vilt och manniskor, sexism/rasism = uteslutning</div>
          <div style={{ marginBottom: 4 }}>8. Overtradelser — jaktledaren kan forverka jaktratt, max resterande jaktaret</div>
          <div>9. Vapen och legitimation — vapenlicens, jaktkort och leg ska alltid finnas tillgangligt, vapen kontrollerade och inskjutna</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onCancel} style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '10px 16px', cursor: 'pointer', flex: 1 }}>Avbryt</button>
        <button onClick={() => setPreview(true)} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 4, padding: '10px 16px', fontWeight: 700, cursor: 'pointer', flex: 2 }}>Forhandsgranska</button>
      </div>
    </div>
  );
}

// ── REQUIREMENTS + QUALIFICATIONS PANEL ────────────────────────
export function RequirementsAndQualificationsPanel({ markId, user }: { markId: string; user: QidUser | null }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [requirements, setRequirements] = useState<MarkRequirements | null>(null);
  const [profileCheck, setProfileCheck] = useState<ProfileCheck | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    gql(`query MR($markId: ID!) { markRequirements(markId: $markId) { id minExperienceYears requiresWeaponLicense requiresDog afterhuntRequired customRequirements { label required } requirementsVisible } }`, { markId })
      .then(d => { setRequirements(d.markRequirements); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [markId]);

  useEffect(() => {
    if (!user) return;
    fetch(resolveUrl('/api/profile'), { headers: { ...bearerHeaders(localStorage.getItem('qvenari_access_token') || '') } })
      .then(r => r.json())
      .then(d => setProfile(d))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (profile && requirements) {
      const p = {
        hunterSinceYear: profile.hunting_region_year || profile.hunter_since_year,
        licenseUrls: (() => { try { return JSON.parse(profile.license_urls_json || '[]'); } catch { return []; } })(),
        weapons: (() => { try { return JSON.parse(profile.weapons_json || '[]'); } catch { return []; } })(),
        dogs: (() => { try { return JSON.parse(profile.dogs_json || '[]'); } catch { return []; } })(),
        afterhuntCertified: profile.afterhunt_certified || profile.eftersok_certified,
      };
      setProfileCheck(checkProfileAgainstRequirements(p, requirements));
    }
  }, [profile, requirements]);

  if (!loaded) return null;

  const hasReqs = requirements && (
    requirements.minExperienceYears > 0 || requirements.requiresWeaponLicense ||
    requirements.requiresDog || requirements.afterhuntRequired ||
    (requirements.customRequirements?.some(r => r.required))
  );

  if (!hasReqs && !profile) return null;

  return (
    <div style={{ marginBottom: 12, marginTop: 8 }}>
      {/* Kravprofil */}
      {hasReqs && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 14px', marginBottom: 8 }}>
          <div style={{ color: T.textSub, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Kravprofil</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {requirements!.minExperienceYears > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {profileCheck ? (
                  <span style={{ fontSize: 14 }}>{profileCheck.checks.find(c => c.label.includes('erfarenhet'))?.met ? '✅' : '❌'}</span>
                ) : <span style={{ color: T.textSub, fontSize: 14 }}>📋</span>}
                <span style={{ color: T.text, fontSize: 13 }}>Minst {requirements!.minExperienceYears} års jägarerfarenhet</span>
                {profileCheck && (
                  <span style={{ color: T.textSub, fontSize: 11, marginLeft: 'auto' }}>{profileCheck.checks.find(c => c.label.includes('erfarenhet'))?.detail}</span>
                )}
              </div>
            )}
            {requirements!.requiresWeaponLicense && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {profileCheck ? (
                  <span style={{ fontSize: 14 }}>{profileCheck.checks.find(c => c.label.includes('apenlicens'))?.met ? '✅' : '❌'}</span>
                ) : <span style={{ color: T.textSub, fontSize: 14 }}>📋</span>}
                <span style={{ color: T.text, fontSize: 13 }}>Vapenlicens krävs</span>
                {profileCheck && (
                  <span style={{ color: T.textSub, fontSize: 11, marginLeft: 'auto' }}>{profileCheck.checks.find(c => c.label.includes('apenlicens'))?.detail}</span>
                )}
              </div>
            )}
            {requirements!.requiresDog && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {profileCheck ? (
                  <span style={{ fontSize: 14 }}>{profileCheck.checks.find(c => c.label.includes('akthund'))?.met ? '✅' : '❌'}</span>
                ) : <span style={{ color: T.textSub, fontSize: 14 }}>📋</span>}
                <span style={{ color: T.text, fontSize: 13 }}>Jakthund krävs</span>
                {profileCheck && (
                  <span style={{ color: T.textSub, fontSize: 11, marginLeft: 'auto' }}>{profileCheck.checks.find(c => c.label.includes('akthund'))?.detail}</span>
                )}
              </div>
            )}
            {requirements!.afterhuntRequired && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {profileCheck ? (
                  <span style={{ fontSize: 14 }}>{profileCheck.checks.find(c => c.label.includes('fters'))?.met ? '✅' : '❌'}</span>
                ) : <span style={{ color: T.textSub, fontSize: 14 }}>📋</span>}
                <span style={{ color: T.text, fontSize: 13 }}>Eftersöksutbildning krävs</span>
                {profileCheck && (
                  <span style={{ color: T.textSub, fontSize: 11, marginLeft: 'auto' }}>{profileCheck.checks.find(c => c.label.includes('fters'))?.detail}</span>
                )}
              </div>
            )}
            {requirements!.customRequirements?.filter(r => r.required).map((cr, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: T.textSub, fontSize: 14 }}>📋</span>
                <span style={{ color: T.text, fontSize: 13 }}>{cr.label}</span>
                <span style={{ color: T.textSub, fontSize: 11, marginLeft: 'auto' }}>Verifieras manuellt</span>
              </div>
            ))}
          </div>
          {profileCheck && !profileCheck.meetsAll && (
            <div style={{ marginTop: 10, padding: '7px 10px', background: 'rgba(230,76,60,0.08)', border: '1px solid rgba(230,76,60,0.25)', borderRadius: 5, color: '#e74c3c', fontSize: 12 }}>
              Du uppfyller inte alla krav — du kan ändå ansöka via markens annonser; marken bedömer din profil.
            </div>
          )}
          {profileCheck && profileCheck.meetsAll && profileCheck.checks.length > 0 && (
            <div style={{ marginTop: 10, padding: '7px 10px', background: 'rgba(97,216,54,0.08)', border: '1px solid rgba(97,216,54,0.25)', borderRadius: 5, color: '#61D836', fontSize: 12 }}>
              Du uppfyller alla krav ✅
            </div>
          )}
        </div>
      )}

      {/* Jägarens kvalifikationer (om inloggad och profil finns) */}
      {user && profile && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ color: T.textSub, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Dina kvalifikationer</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(profile.hunting_region_year || profile.hunter_since_year) && (
              <span style={{ background: `${T.border}`, color: T.text, fontSize: 12, padding: '3px 9px', borderRadius: 20 }}>
                🎯 {new Date().getFullYear() - parseInt(profile.hunting_region_year || profile.hunter_since_year)} års erfarenhet
              </span>
            )}
            {(() => { try { return JSON.parse(profile.license_urls_json || '[]'); } catch { return []; } })().length > 0 && (
              <span style={{ background: `${T.border}`, color: T.text, fontSize: 12, padding: '3px 9px', borderRadius: 20 }}>🔫 Vapenlicens</span>
            )}
            {(() => { try { return JSON.parse(profile.weapons_json || '[]'); } catch { return []; } })().length > 0 && (
              <span style={{ background: `${T.border}`, color: T.text, fontSize: 12, padding: '3px 9px', borderRadius: 20 }}>
                🔫 {(() => { try { return JSON.parse(profile.weapons_json || '[]'); } catch { return []; } })().length} vapen
              </span>
            )}
            {(() => { try { return JSON.parse(profile.dogs_json || '[]'); } catch { return []; } })().length > 0 && (
              <span style={{ background: `${T.border}`, color: T.text, fontSize: 12, padding: '3px 9px', borderRadius: 20 }}>
                🐕 {(() => { try { return JSON.parse(profile.dogs_json || '[]'); } catch { return []; } })().length} jakthund(ar)
              </span>
            )}
            {(profile.afterhunt_certified || profile.eftersok_certified) && (
              <span style={{ background: `${T.border}`, color: T.text, fontSize: 12, padding: '3px 9px', borderRadius: 20 }}>✅ Eftersöksutbildad</span>
            )}
            {!profile.hunting_region_year && !profile.hunter_since_year &&
             !(() => { try { return JSON.parse(profile.license_urls_json || '[]'); } catch { return []; } })().length &&
             !(() => { try { return JSON.parse(profile.weapons_json || '[]'); } catch { return []; } })().length &&
             !(() => { try { return JSON.parse(profile.dogs_json || '[]'); } catch { return []; } })().length &&
             !(profile.afterhunt_certified || profile.eftersok_certified) && (
              <span style={{ color: T.textSub, fontSize: 12 }}>Inga kvalifikationer i din profil — fyll i din profil för bättre matchning.</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── APPLY BUTTON (bottom of mark/hunt detail) ──────────────────
/** `variant="cta"`: jaktsidans bokningskort — orange knapp "Ansök om plats", utan kravpanel (den visas separat på sidan). */
/**
 * Status för jägarens äldre ansökan till marken (mark_applications). D5: nya ansökningar görs inte här –
 * utan ansökan pekar knappen på markens annonser. Pågående äldre ansökningar (avtal, signering, betalning) slutförs som förut.
 */
export function ApplyButton({ mark, user, onLogin, variant = 'default', onOpenAd }: { mark: Mark; user: QidUser | null; onLogin: () => void; variant?: 'default' | 'cta'; onOpenAd?: (ad: MarkAd) => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const cta = variant === 'cta';
  const mt = cta ? 0 : 12;
  const primaryBtn: React.CSSProperties = cta
    ? { width: '100%', background: T.accentFill, color: T.onAccent, border: 'none', borderRadius: 12, padding: '15px', fontSize: 16, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }
    : { width: '100%', background: T.amber, color: '#000', border: 'none', borderRadius: 8, padding: '13px', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 12 };
  const [open, setOpen] = useState(false);
  const [existingApp, setExistingApp] = useState<MarkApplication | null>(null);
  const [loading, setLoading] = useState(false);
  const [qualified, setQualified] = useState(false);

  const reload = () => {
    if (!user) return;
    setLoading(true);
    gql(`query MyApps { myApplications { id markId status interestRoles contract { id status signedAt paymentStatus amount swishNumber contractText ownerSignedAt ownerSignatureName } } }`)
      .then(d => {
        const apps = d.myApplications || [];
        const match = apps.find((a: any) => a.markId === mark.id && !['rejected', 'dispensation_denied'].includes(a.status));
        setExistingApp(match || null);
        setQualified(match?.status === 'qualified' || match?.status === 'completed');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, [user, mark.id]);

  if (loading) return null;

  // ── Fully qualified ──
  if (qualified) {
    return (
      <div style={{ width: '100%', background: 'rgba(97,216,54,0.12)', border: '2px solid #61D836', borderRadius: 8, padding: '12px 16px', marginTop: mt, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 20 }}>✅</span>
        <div>
          <div style={{ color: '#61D836', fontSize: 14, fontWeight: 700 }}>Kvalificerad</div>
          <div style={{ color: T.textSub, fontSize: 12 }}>Du är godkänd och kan direkt delta på jakter på denna mark</div>
        </div>
      </div>
    );
  }

  // ── Has active contract (needs signing or payment) ──
  if (existingApp?.contract && existingApp.status !== 'user_signed') {
    const c = existingApp.contract as any;
    return (
      <>
        <button onClick={() => setOpen(true)} style={primaryBtn}>
          {c.status === 'pending_signature' ? 'Signera avtalet' :
           c.paymentStatus === 'unpaid' ? 'Betala med kort' : 'Visa ditt avtal'}
        </button>
        {open && <ContractModal contract={existingApp.contract} mark={mark} onClose={() => setOpen(false)} onUpdate={() => { setOpen(false); reload(); }} />}
      </>
    );
  }

  // ── Waiting for owner countersignature ──
  if (existingApp?.status === 'user_signed') {
    return (
      <div style={{ width: '100%', background: 'rgba(155,89,182,0.1)', border: '1px solid rgba(155,89,182,0.4)', borderRadius: 8, padding: '12px 16px', marginTop: mt, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 18 }}>⏳</span>
        <div>
          <div style={{ color: '#9b59b6', fontSize: 14, fontWeight: 700 }}>Väntar på markägarens signatur</div>
          <div style={{ color: T.textSub, fontSize: 12 }}>Du har signerat. Markägaren granskar och signerar — du meddelas när du är kvalificerad.</div>
        </div>
      </div>
    );
  }

  // ── Has pending question from admin ──
  if (existingApp?.status === 'question_asked') {
    return (
      <>
        <button onClick={() => setOpen(true)} style={{ ...primaryBtn, background: '#00A2FF', color: '#fff' }}>
          Fråga från admin — svara nu
        </button>
        {open && <QuestionResponseModal applicationId={existingApp.id} onClose={() => setOpen(false)} />}
      </>
    );
  }

  // ── Has existing app in progress ──
  if (existingApp && !['rejected', 'dispensation_denied'].includes(existingApp.status)) {
    const st = statusLabel(existingApp.status);
    return (
      <div style={{ width: '100%', background: `${st.color}15`, border: `1px solid ${st.color}33`, borderRadius: 8, padding: 13, marginTop: mt, textAlign: 'center' }}>
        <span style={{ color: st.color, fontSize: 14, fontWeight: 700 }}>{st.label}</span>
        {(existingApp as any).interestRoles?.length > 0 && (
          <div style={{ color: T.textSub, fontSize: 11, marginTop: 4 }}>
            Roller: {((existingApp as any).interestRoles as string[]).map((r: string) => INTEREST_ROLES.find(x => x.id === r)?.label || r).join(', ')}
          </div>
        )}
      </div>
    );
  }

  // ── Ingen ansökan: D5 – nya ansökningar görs via markens annonser (inloggning sker där) ──
  return (
    <>
      {!cta && <RequirementsAndQualificationsPanel markId={mark.id} user={user} />}
      <MarkAdsPointer markId={mark.id} onOpenAd={onOpenAd} cta={cta} />
    </>
  );
}

// ── QUESTION RESPONSE MODAL ────────────────────────────────────
function QuestionResponseModal({ applicationId, onClose }: { applicationId: string; onClose: () => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [question, setQuestion] = useState('');
  const [response, setResponse] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    gql(`query MyApps { myApplications { id adminQuestion status } }`)
      .then(d => {
        const app = (d.myApplications || []).find((a: any) => a.id === applicationId);
        if (app?.adminQuestion) setQuestion(app.adminQuestion);
      }).catch(() => {});
  }, [applicationId]);

  async function submit() {
    setBusy(true);
    try {
      await gql(`mutation RQ($id: ID!, $response: String!) { respondToQuestion(applicationId: $id, response: $response) { id status } }`, { id: applicationId, response });
      setDone(true);
    } catch (e: any) { alert(e.message); }
    setBusy(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 28, width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ color: T.text, margin: 0, fontSize: 18 }}>Fraga fran admin</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 20 }}>x</button>
        </div>
        {done ? (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <div style={{ color: T.text, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Svar skickat!</div>
            <button onClick={() => { onClose(); window.location.reload(); }} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 5, padding: '10px 20px', cursor: 'pointer', marginTop: 16 }}>Stang</button>
          </div>
        ) : (
          <>
            <div style={{ background: 'rgba(0,162,255,0.08)', border: '1px solid rgba(0,162,255,0.25)', borderRadius: 5, padding: 14, marginBottom: 20 }}>
              <div style={{ color: '#00A2FF', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, marginBottom: 6 }}>Fraga</div>
              <div style={{ color: T.text, fontSize: 14, lineHeight: 1.6 }}>{question}</div>
            </div>
            <textarea value={response} onChange={e => setResponse(e.target.value)} placeholder="Skriv ditt svar..." style={{ width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '10px 14px', color: T.text, fontSize: 14, minHeight: 100, resize: 'vertical' as const, outline: 'none', boxSizing: 'border-box' as const, marginBottom: 16 }} />
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={onClose} style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 5, padding: '10px 20px', cursor: 'pointer', flex: 1 }}>Avbryt</button>
              <button onClick={submit} disabled={busy || !response.trim()} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 5, padding: '10px 20px', fontWeight: 700, cursor: 'pointer', flex: 2 }}>{busy ? 'Skickar...' : 'Skicka svar'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── LEGAL CONTRACT DOCUMENT (professional white-background document) ──────────
function renderContractSections(text: string) {
  if (!text) return <p style={{ fontSize: 13, color: '#888', fontStyle: 'italic' }}>Ingen avtalstext angiven.</p>;
  const blocks = text.split(/\n\n+/).filter(p => p.trim());
  let paraNum = 0;
  return (
    <>
      {blocks.map((block, i) => {
        const trimmed = block.trim();
        // Detect section headers: §N, digits+dot, all-caps lines, **bold**, # markdown
        const isHeader = /^(§\s*\d|[\d]+\.\s+[A-ZÅÄÖ]|#{1,3}\s|[A-ZÅÄÖ][A-ZÅÄÖ\s]{4,}$|\*\*[^*]+\*\*$)/.test(trimmed);
        if (isHeader) {
          const clean = trimmed.replace(/^#+\s*/, '').replace(/\*\*/g, '');
          return (
            <div key={i} style={{ marginTop: 18, marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'Arial, sans-serif', color: '#1a1a1a', borderBottom: '1px solid #e0ddd8', paddingBottom: 3 }}>{clean}</div>
            </div>
          );
        }
        paraNum++;
        return (
          <div key={i} style={{ marginBottom: 10, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ fontSize: 9, color: '#aaa', fontFamily: 'Arial, sans-serif', minWidth: 24, marginTop: 3, userSelect: 'none' as const }}>§ {paraNum}</div>
            <div style={{ fontSize: 11, lineHeight: 1.75, flex: 1, color: '#222' }}>{trimmed}</div>
          </div>
        );
      })}
    </>
  );
}

function downloadAsPDF(ref: React.RefObject<HTMLDivElement | null>, filename: string) {
  const el = ref.current;
  if (!el) return;
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${filename}</title>
<style>
  body { margin: 0; background: #fff; }
  @media print { @page { margin: 12mm; } }
</style>
</head><body>${el.outerHTML}</body></html>`;
  printHtml(html, filename).catch((e: unknown) => alert(e instanceof Error ? e.message : 'Utskriften gick inte att starta.'));
}

export function LegalContractDocument({
  contractText, markName, amount, signatureName, signedAt,
  applicantEmail, contractRef, createdAt, showSignaturePlaceholder,
}: {
  contractText: string; markName?: string; amount?: number;
  signatureName?: string; signedAt?: string; applicantEmail?: string;
  contractRef?: string; createdAt?: string; showSignaturePlaceholder?: boolean;
}) {
  const docRef = React.useRef<HTMLDivElement>(null);
  const today = new Date().toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });
  const refId = contractRef || 'QV-DRAFT';
  const dateStr = createdAt ? new Date(createdAt).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' }) : today;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button onClick={() => downloadAsPDF(docRef, `Jakträttsavtal-${refId}.pdf`)} style={{ background: '#EE8F09', color: '#fff', border: 'none', borderRadius: 5, padding: '8px 16px', fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>⬇</span> Ladda ner PDF
        </button>
      </div>
    <div ref={docRef} style={{ background: '#ffffff', color: '#1a1a1a', fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 1.65, padding: '28px 32px', border: '1px solid #ccc', borderRadius: 3, margin: '0 auto', boxShadow: '0 3px 12px rgba(0,0,0,0.15)' }}>
      {/* Document header */}
      <div style={{ textAlign: 'center', borderBottom: '3px double #1a1a1a', paddingBottom: 18, marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
          <img src="/brand/deer-icon.png" alt="" style={{ width: 22, height: 22, opacity: 0.7 }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span style={{ fontSize: 9, fontFamily: 'Arial, sans-serif', letterSpacing: '0.3em', textTransform: 'uppercase' as const, color: '#888' }}>venari · Jakt &amp; Viltvård · Marknadsplats</span>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, marginBottom: 5 }}>JAKTRÄTTSAVTAL</div>
        <div style={{ fontSize: 10, color: '#666', fontFamily: 'Arial, sans-serif', letterSpacing: '0.05em' }}>
          Avtalsreferens: <strong>{refId}</strong> &nbsp;·&nbsp; Upprättat: {dateStr}
        </div>
        <div style={{ marginTop: 8, fontSize: 9, color: '#999', fontFamily: 'Arial, sans-serif', fontStyle: 'italic' }}>
          Avtalet ingås direkt mellan Upplåtaren och Jakträttstagaren. venari är en digital marknadsplats som förmedlar kontakten — marknadsplatsen är ej part i detta avtal.
        </div>
      </div>

      {/* Avtalsparter */}
      <section style={{ marginBottom: 22 }}>
        <div style={{ fontSize: 10, fontFamily: 'Arial, sans-serif', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' as const, color: '#555', borderBottom: '1.5px solid #c8c4bc', paddingBottom: 4, marginBottom: 12 }}>AVTALSPARTER</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 12 }}>
          <div style={{ borderLeft: '3px solid #EE8F09', paddingLeft: 12 }}>
            <div style={{ fontSize: 8, fontFamily: 'Arial, sans-serif', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.12em', color: '#999', marginBottom: 5 }}>Part 1 — Upplåtaren</div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{markName || 'Jaktmark'}</div>
            <div style={{ color: '#444', fontSize: 10, fontFamily: 'Arial, sans-serif', marginTop: 2 }}>Markägare / Jakträttsinnehavare</div>
            <div style={{ color: '#666', fontSize: 10, fontFamily: 'Arial, sans-serif', marginTop: 3, fontStyle: 'italic' }}>Nedan benämnd "Upplåtaren"</div>
          </div>
          <div style={{ borderLeft: '3px solid #6b6b6b', paddingLeft: 12 }}>
            <div style={{ fontSize: 8, fontFamily: 'Arial, sans-serif', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.12em', color: '#999', marginBottom: 5 }}>Part 2 — Jakträttstagaren</div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{signatureName || applicantEmail || '—'}</div>
            {applicantEmail && signatureName && <div style={{ color: '#555', fontSize: 10, fontFamily: 'Arial, sans-serif' }}>{applicantEmail}</div>}
            <div style={{ color: '#444', fontSize: 10, fontFamily: 'Arial, sans-serif', marginTop: 2 }}>Jägare / Sökande</div>
            <div style={{ color: '#666', fontSize: 10, fontFamily: 'Arial, sans-serif', marginTop: 3, fontStyle: 'italic' }}>Nedan benämnd "Jakträttstagaren"</div>
          </div>
        </div>
        <div style={{ marginTop: 12, padding: '8px 14px', background: '#faf8f5', border: '1px solid #e8e4dc', borderRadius: 3, fontSize: 11, color: '#555', fontFamily: 'Arial, sans-serif', lineHeight: 1.55 }}>
          Parterna har denna dag ingått följande avtal avseende jakträtt och tillhörande rättigheter och skyldigheter. Avtalet regleras av Jaktlagen (1987:259), Jaktförordningen (1987:905) och övrig tillämplig svensk rätt.
        </div>
      </section>

      {/* Definitioner */}
      <section style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 10, fontFamily: 'Arial, sans-serif', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' as const, color: '#555', borderBottom: '1.5px solid #c8c4bc', paddingBottom: 4, marginBottom: 10 }}>DEFINITIONER</div>
        {[
          { term: 'Jaktmark', def: `Den fastighet eller det område som tillhör "${markName || 'Jaktmarken'}" och som avses i detta avtal.` },
          { term: 'Jakträtten', def: 'Den rätt att bedriva jakt på Jaktmarken som Jakträttstagaren tilldelas genom detta avtal.' },
          { term: 'Avtalsperiod', def: 'Den tidsperiod under vilken detta avtal är gällande och jakträtten utövas.' },
          { term: 'Jaktledare', def: 'Den person inom jaktlaget som ansvarar för den praktiska organisationen av jakt och säkerhet på Jaktmarken.' },
          { term: 'Marknadsplatsen', def: 'venari (qvenari.com) — den digitala plattform via vilken parterna funnit varandra. Marknadsplatsen är ej part i detta avtal.' },
        ].map(({ term, def }) => (
          <div key={term} style={{ display: 'flex', gap: 10, marginBottom: 6, fontSize: 11 }}>
            <div style={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, minWidth: 120, color: '#333' }}>{term}:</div>
            <div style={{ color: '#444', flex: 1 }}>{def}</div>
          </div>
        ))}
      </section>

      {/* Avtalsvillkor (the template body text) */}
      <section style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, fontFamily: 'Arial, sans-serif', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' as const, color: '#555', borderBottom: '1.5px solid #c8c4bc', paddingBottom: 4, marginBottom: 12 }}>AVTALSVILLKOR</div>
        {renderContractSections(contractText)}
      </section>

      {/* Betalningsvillkor */}
      {amount != null && amount > 0 && (
        <section style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontFamily: 'Arial, sans-serif', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' as const, color: '#555', borderBottom: '1.5px solid #c8c4bc', paddingBottom: 4, marginBottom: 10 }}>BETALNINGSVILLKOR</div>
          <div style={{ background: '#faf8f5', border: '1px solid #e0dbd0', borderRadius: 3, padding: '12px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <div style={{ fontSize: 12 }}>Avgift att erläggas vid avtalstecknandet:</div>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'Arial, sans-serif' }}>{amount.toLocaleString('sv-SE')} kr</div>
            </div>
            <div style={{ fontSize: 10, color: '#777', fontFamily: 'Arial, sans-serif', lineHeight: 1.55 }}>Betalning sker med kort via Qvenari (Stripe) i samband med signering. Qvenari betalar ut avgiften till Upplåtaren efter avdrag för plattformsavgift. Avtalet träder i kraft när betalning bekräftats. Kvitto tillhandahålls inom 48 timmar.</div>
          </div>
        </section>
      )}

      {/* Allmänna bestämmelser */}
      <section style={{ marginBottom: 22 }}>
        <div style={{ fontSize: 10, fontFamily: 'Arial, sans-serif', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' as const, color: '#555', borderBottom: '1.5px solid #c8c4bc', paddingBottom: 4, marginBottom: 10 }}>ALLMÄNNA BESTÄMMELSER</div>
        {[
          { par: 'A', title: 'Tillämplig lag', text: 'Detta avtal tolkas och regleras av svensk rätt, inklusive Jaktlagen (1987:259), Vapenlagen (1996:67) och Allemansrätten. Jakträttstagaren förbinder sig att följa samtliga tillämpliga föreskrifter från Naturvårdsverket och länsstyrelsen.' },
          { par: 'B', title: 'Ansvar och säkerhet', text: 'Jakträttstagaren ansvarar för att jakten bedrivs på ett säkert och etiskt sätt i enlighet med god jaktsed. Upplåtaren är inte ansvarig för skador som drabbar Jakträttstagaren eller tredje man till följd av jaktutövningen, om inte skadan orsakats av grov vårdslöshet från Upplåtarens sida.' },
          { par: 'C', title: 'Marknadsplatsens roll', text: 'venari är en digital marknadsplats som tillhandahåller infrastruktur för kontaktskapande, avtalshantering och betalningsförmedling. Marknadsplatsen bär inget ansvar för avtalets fullgörande mellan parterna och är ej part i detta avtal.' },
          { par: 'D', title: 'Force majeure', text: 'Part är befriad från fullgörande av sina förpliktelser i den mån detta hindras av omständighet utanför parts rimliga kontroll, såsom naturkatastrof, krig, pandemi, lagstiftning eller myndighetsbeslut.' },
          { par: 'E', title: 'Ändringar i avtalet', text: 'Ändringar av eller tillägg till detta avtal kräver skriftlig överenskommelse signerad av båda parter för att vara giltiga.' },
          { par: 'F', title: 'Tvistlösning', text: 'Tvist angående tolkning eller tillämpning av detta avtal skall i första hand lösas genom förhandling. Kan parterna inte nå enighet skall tvisten avgöras av allmän domstol med tingsrätten i det härad där Jaktmarken är belägen som första instans.' },
        ].map(({ par, title, text }) => (
          <div key={par} style={{ marginBottom: 8, display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 11 }}>
            <div style={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#888', minWidth: 16 }}>{par}.</div>
            <div style={{ flex: 1, color: '#333', lineHeight: 1.65 }}><span style={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#1a1a1a' }}>{title}. </span>{text}</div>
          </div>
        ))}
      </section>

      {/* Underskrifter */}
      <section style={{ borderTop: '2px solid #1a1a1a', paddingTop: 18 }}>
        <div style={{ fontSize: 10, fontFamily: 'Arial, sans-serif', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' as const, color: '#555', marginBottom: 8 }}>UNDERSKRIFTER</div>
        <div style={{ fontSize: 11, color: '#555', marginBottom: 18, fontStyle: 'italic' }}>
          Parterna intygar att de tagit del av och accepterar samtliga villkor i detta avtal. Avtalet är juridiskt bindande från och med Jakträttstagarens underskrift och bekräftad betalning.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
          <div>
            <div style={{ fontSize: 9, fontFamily: 'Arial, sans-serif', color: '#999', textTransform: 'uppercase' as const, letterSpacing: '0.12em', marginBottom: 24 }}>Part 1 — Upplåtaren</div>
            <div style={{ borderTop: '1px solid #888', paddingTop: 5 }}>
              <div style={{ fontSize: 9, color: '#888', fontFamily: 'Arial, sans-serif' }}>Underskrift &amp; namnförtydligande</div>
              <div style={{ fontSize: 9, color: '#aaa', fontFamily: 'Arial, sans-serif', marginTop: 4 }}>Ort och datum</div>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 9, fontFamily: 'Arial, sans-serif', color: '#999', textTransform: 'uppercase' as const, letterSpacing: '0.12em', marginBottom: 8 }}>Part 2 — Jakträttstagaren</div>
            {signatureName ? (
              <div style={{ background: '#f0faf0', border: '1px solid #4CAF50', borderRadius: 4, padding: '8px 12px', marginTop: 4 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Georgia, serif', letterSpacing: '0.03em' }}>{signatureName}</div>
                <div style={{ fontSize: 9, color: '#4CAF50', fontFamily: 'Arial, sans-serif', marginTop: 3 }}>✓ Elektronisk signatur</div>
                {signedAt && <div style={{ fontSize: 9, color: '#666', fontFamily: 'Arial, sans-serif', marginTop: 1 }}>{new Date(signedAt).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })}</div>}
              </div>
            ) : showSignaturePlaceholder ? (
              <div style={{ borderTop: '1px solid #888', paddingTop: 5, marginTop: 20 }}>
                <div style={{ fontSize: 9, color: '#888', fontFamily: 'Arial, sans-serif' }}>Underskrift &amp; namnförtydligande</div>
                <div style={{ fontSize: 9, color: '#aaa', fontFamily: 'Arial, sans-serif', marginTop: 4 }}>Ort och datum</div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Document footer */}
        <div style={{ marginTop: 22, paddingTop: 10, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', fontSize: 8, fontFamily: 'Arial, sans-serif', color: '#bbb', letterSpacing: '0.05em' }}>
          <span>venari · qvenari.com · Digital marknadsplats för jakt</span>
          <span>Ref: {refId}</span>
          <span>Upprättat elektroniskt via marknadsplatsen</span>
        </div>
      </section>
    </div>
    </div>
  );
}

// ── GÄSTJAKTSAVTAL — Upplåtaren ↔ Deltagaren ────────────────────
// Används när en jägare godkänts/bjudits in för en enskild jakt
// eller ett jaktpaket på en jaktmark.
export function GastjaktsAvtal({
  markName, huntName, huntDates, numHunts, isPackage,
  amount, pricePerHunt,
  signatureName, signedAt, applicantEmail,
  contractRef, createdAt, showSignaturePlaceholder,
}: {
  markName?: string; huntName?: string; huntDates?: string[]; numHunts?: number; isPackage?: boolean;
  amount?: number; pricePerHunt?: number;
  signatureName?: string; signedAt?: string; applicantEmail?: string;
  contractRef?: string; createdAt?: string; showSignaturePlaceholder?: boolean;
}) {
  const today = new Date().toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });
  const refId = contractRef || 'QV-GJ-DRAFT';
  const dateStr = createdAt ? new Date(createdAt).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' }) : today;
  const n = numHunts || 1;
  const datesStr = huntDates && huntDates.length > 0
    ? huntDates.map(d => new Date(d).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })).join(', ')
    : 'Enligt separat inbjudan';
  const docRef2 = React.useRef<HTMLDivElement>(null);
  const S = {
    doc: { background: '#ffffff', color: '#1a1a1a', fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 1.65, padding: '28px 32px', border: '1px solid #ccc', borderRadius: 3, margin: '0 auto', boxShadow: '0 3px 12px rgba(0,0,0,0.15)' } as React.CSSProperties,
    sectionHeading: { fontSize: 10, fontFamily: 'Arial, sans-serif', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' as const, color: '#555', borderBottom: '1.5px solid #c8c4bc', paddingBottom: 4, marginBottom: 12 } as React.CSSProperties,
    para: { marginBottom: 8, display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 11 } as React.CSSProperties,
    parNum: { fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#888', minWidth: 22, marginTop: 1 } as React.CSSProperties,
    parBody: { flex: 1, color: '#333', lineHeight: 1.65 } as React.CSSProperties,
    parTitle: { fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#1a1a1a' } as React.CSSProperties,
    defRow: { display: 'flex', gap: 10, marginBottom: 6, fontSize: 11 } as React.CSSProperties,
    defTerm: { fontFamily: 'Arial, sans-serif', fontWeight: 700, minWidth: 150, color: '#333' } as React.CSSProperties,
  };
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button onClick={() => downloadAsPDF(docRef2, `${isPackage ? 'Jaktpaketavtal' : 'Gästjaktsavtal'}-${refId}.pdf`)} style={{ background: '#EE8F09', color: '#fff', border: 'none', borderRadius: 5, padding: '8px 16px', fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>⬇</span> Ladda ner PDF
        </button>
      </div>
    <div ref={docRef2} style={S.doc}>
      {/* ── Dokumenthuvud ── */}
      <div style={{ textAlign: 'center', borderBottom: '3px double #1a1a1a', paddingBottom: 18, marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
          <img src="/brand/deer-icon.png" alt="" style={{ width: 22, height: 22, opacity: 0.7 }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span style={{ fontSize: 9, fontFamily: 'Arial, sans-serif', letterSpacing: '0.3em', textTransform: 'uppercase' as const, color: '#888' }}>venari · Jakt &amp; Viltvård · Marknadsplats</span>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, marginBottom: 4 }}>
          {isPackage ? 'JAKTPAKETAVTAL' : 'GÄSTJAKTSAVTAL'}
        </div>
        <div style={{ fontSize: 11, color: '#555', fontFamily: 'Arial, sans-serif', marginBottom: 6 }}>
          {isPackage
            ? `Avtal om deltagande i jaktpaket om ${n} jakter på ${markName || 'Jaktmarken'}`
            : `Avtal om deltagande i jakt på ${markName || 'Jaktmarken'}`}
        </div>
        <div style={{ fontSize: 10, color: '#666', fontFamily: 'Arial, sans-serif', letterSpacing: '0.05em' }}>
          Avtalsreferens: <strong>{refId}</strong> &nbsp;·&nbsp; Upprättat: {dateStr}
        </div>
        <div style={{ marginTop: 8, fontSize: 9, color: '#999', fontFamily: 'Arial, sans-serif', fontStyle: 'italic' }}>
          Avtalet ingås direkt mellan Upplåtaren och Deltagaren. venari är en digital marknadsplats som förmedlar kontakten — marknadsplatsen är ej part i detta avtal.
        </div>
      </div>

      {/* ── Avtalsparter ── */}
      <section style={{ marginBottom: 22 }}>
        <div style={S.sectionHeading}>AVTALSPARTER</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 12 }}>
          <div style={{ borderLeft: '3px solid #EE8F09', paddingLeft: 12 }}>
            <div style={{ fontSize: 8, fontFamily: 'Arial, sans-serif', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.12em', color: '#999', marginBottom: 5 }}>Part 1 — Upplåtaren</div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{markName || 'Jaktmark'}</div>
            <div style={{ color: '#444', fontSize: 10, fontFamily: 'Arial, sans-serif', marginTop: 2 }}>Markägare / Jakträttsinnehavare</div>
            <div style={{ color: '#666', fontSize: 10, fontFamily: 'Arial, sans-serif', marginTop: 3, fontStyle: 'italic' }}>Nedan benämnd "Upplåtaren"</div>
          </div>
          <div style={{ borderLeft: '3px solid #6b6b6b', paddingLeft: 12 }}>
            <div style={{ fontSize: 8, fontFamily: 'Arial, sans-serif', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.12em', color: '#999', marginBottom: 5 }}>Part 2 — Deltagaren</div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{signatureName || applicantEmail || '—'}</div>
            {applicantEmail && signatureName && <div style={{ color: '#555', fontSize: 10, fontFamily: 'Arial, sans-serif' }}>{applicantEmail}</div>}
            <div style={{ color: '#444', fontSize: 10, fontFamily: 'Arial, sans-serif', marginTop: 2 }}>Jägare / Gästdeltagare</div>
            <div style={{ color: '#666', fontSize: 10, fontFamily: 'Arial, sans-serif', marginTop: 3, fontStyle: 'italic' }}>Nedan benämnd "Deltagaren"</div>
          </div>
        </div>
        <div style={{ marginTop: 12, padding: '8px 14px', background: '#faf8f5', border: '1px solid #e8e4dc', borderRadius: 3, fontSize: 11, color: '#555', fontFamily: 'Arial, sans-serif', lineHeight: 1.55 }}>
          Parterna har ingått följande avtal avseende rätt att delta i jakt på Upplåtarens mark.
          Avtalet regleras av Jaktlagen (1987:259), Jaktförordningen (1987:905), Vapenlagen (1996:67) och övrig tillämplig svensk rätt.
        </div>
      </section>

      {/* ── Jaktens detaljer ── */}
      <section style={{ marginBottom: 18 }}>
        <div style={S.sectionHeading}>JAKTENS DETALJER</div>
        <div style={{ background: '#faf8f5', border: '1px solid #e8e4dc', borderRadius: 3, padding: '12px 16px', fontSize: 11 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px' }}>
            {huntName && (
              <><div style={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#555' }}>Jaktbenämning:</div>
              <div style={{ color: '#1a1a1a', fontWeight: 600 }}>{huntName}</div></>
            )}
            <div style={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#555' }}>Jaktmark:</div>
            <div style={{ color: '#1a1a1a' }}>{markName || '—'}</div>
            <div style={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#555' }}>Avtalets typ:</div>
            <div style={{ color: '#1a1a1a' }}>{isPackage ? `Jaktpaket — ${n} jakter` : 'Enskild gästjakt'}</div>
            <div style={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#555' }}>Datum / Period:</div>
            <div style={{ color: '#1a1a1a' }}>{datesStr}</div>
          </div>
        </div>
      </section>

      {/* ── Definitioner ── */}
      <section style={{ marginBottom: 18 }}>
        <div style={S.sectionHeading}>DEFINITIONER</div>
        {[
          { term: 'Jaktmarken', def: `Det jaktschikte eller den fastighet som tillhör "${markName || 'Upplåtaren'}" och på vilket Deltgarens jakträtt gäller.` },
          { term: 'Gästjakten', def: isPackage ? `Det jaktpaket om ${n} jakter för vilket detta avtal tecknats.` : 'Den enskilda jakt för vilken detta avtal tecknats.' },
          { term: 'Jaktledaren', def: 'Den person som Upplåtaren utsett att leda och ansvara för den praktiska organisationen, säkerheten och ordningen under jakten.' },
          { term: 'Marknadsplatsen', def: 'venari (qvenari.com) — den digitala plattform via vilken parterna funnit varandra. Marknadsplatsen är ej part i detta avtal.' },
          { term: 'Fällt vilt', def: 'Vilt som fälls av Deltagaren under Gästjakten och som ska hanteras i enlighet med villkoren i §5.' },
        ].map(({ term, def }) => (
          <div key={term} style={S.defRow}>
            <div style={S.defTerm}>{term}:</div>
            <div style={{ color: '#444', flex: 1 }}>{def}</div>
          </div>
        ))}
      </section>

      {/* ── Avtalsvillkor ── */}
      <section style={{ marginBottom: 20 }}>
        <div style={S.sectionHeading}>AVTALSVILLKOR</div>
        {[
          {
            n: '§ 1', title: 'Deltagarrätt',
            text: `Upplåtaren ger härmed Deltagaren tillstånd att delta i ${isPackage ? `${n} (${n}) jakttillfällen` : 'ett (1) jakttillfälle'} på Jaktmarken under de datum som framgår av jaktschemat i marknadsplatsen. Rätten är personlig och får ej överlåtas på annan person. Deltagaren äger ej rätt att ta med obesläktade personer utan Upplåtarens skriftliga godkännande.`,
          },
          {
            n: '§ 2', title: 'Säkerhet och vapenhantering',
            text: 'Deltagaren förbinder sig att under hela jakten följa Jaktledarens instruktioner vad gäller säkerhet och ordning. Vapen ska hanteras i enlighet med Vapenlagen och god vapensed. Deltagaren ansvarar för att medförda vapen och ammunition är lagliga och för ändamålet lämpliga. Alkohol eller rusmedel som påverkar omdömesförmågan är förbjudet under jakten.',
          },
          {
            n: '§ 3', title: 'Jaktledarens auktoritet',
            text: 'Deltagaren förbinder sig att ovillkorligen följa Jaktledarens instruktioner under hela jakten. Jaktledaren äger rätt att avbryta deltagandet för en eller samtliga deltagare om säkerhetsregler eller god jaktsed åsidosätts, utan återbetalningsskyldighet för redan erlagd avgift.',
          },
          {
            n: '§ 4', title: 'Dokumentation via venari',
            text: 'Samtliga jakttillfällen som ingår i detta avtal ska startas via marknadsplatsen venari omedelbart före jaktens påbörjan, varvid jakten automatiskt dokumenteras med tid, plats, deltagare och eventuellt fällt vilt. Dokumentation utgör underlag för avskjutningsrapportering och uppfyllande av Jaktlagens krav. Underlåtenhet att dokumentera jakten via venari utgör ett brott mot detta avtal.',
          },
          {
            n: '§ 5', title: 'Fällt vilt och köttfördelning',
            text: 'Fällt vilt tillfaller Uplåtaren om inget annat anges i jaktschemat. Eventuell köttandel som Deltagaren erhåller framgår av respektive jakts specifika villkor. Vid organiserade jakter med gemensam avskjutning gäller Upplåtarens köttfördelningsmodell. Deltagaren ansvarar för att allt fällt vilt hanteras omsorgsfullt och i enlighet med livsmedelshygieniska krav.',
          },
          {
            n: '§ 6', title: 'Eftersök',
            text: 'Vid skadeskjutning är Deltagaren skyldig att omedelbart underrätta Jaktledaren och tillse att godkänt eftersök påbörjas snarast möjligt. Kostnaden för eftersök som orsakas av Deltagarens skadeskjutning bärs av Deltagaren om inget annat avtalats. Deltagaren ska under inga omständigheter lämna jakten utan att skadeskjutning rapporterats.',
          },
          {
            n: '§ 7', title: 'Avbokning och återbetalning',
            text: isPackage
              ? `Avbokning av enstaka jakttillfällen inom paketet ska meddelas minst 7 dagar i förväg. Vid sen avbokning (mindre än 7 dagar) eller uteblivet deltagande utan godkänt laga förfall debiteras fullt pris för det aktuella jakttillfället. Oanvända jakttillfällen i paketet kan ej överlåtas till annan person.`
              : 'Avbokning ska meddelas Upplåtaren senast 72 timmar före jaktens start. Vid senare avbokning eller uteblivet deltagande utan godkänt laga förfall har Upplåtaren rätt att behålla hela avgiften. Laga förfall bedöms av Upplåtaren och kräver styrkt dokumentation.',
          },
          {
            n: '§ 8', title: 'Försäkring och ansvar',
            text: 'Deltagaren ansvarar för att teckna och upprätthålla adekvat jaktansvarsförsäkring för den period avtalet gäller. Upplåtaren ansvarar inte för personskada, sakskada eller förlust som drabbar Deltagaren eller dennes egendom under jakten, om inte skadan orsakats av grov oaktsamhet från Upplåtarens sida. Deltagaren ansvarar för skador som Deltagaren orsakar tredje man eller Upplåtarens egendom.',
          },
        ].map(({ n: num, title, text }) => (
          <div key={num} style={S.para}>
            <div style={S.parNum}>{num}</div>
            <div style={S.parBody}><span style={S.parTitle}>{title}. </span>{text}</div>
          </div>
        ))}
      </section>

      {/* ── Betalningsvillkor ── */}
      {amount != null && amount > 0 && (
        <section style={{ marginBottom: 20 }}>
          <div style={S.sectionHeading}>BETALNINGSVILLKOR</div>
          <div style={{ background: '#faf8f5', border: '1px solid #e0dbd0', borderRadius: 3, padding: '12px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <div style={{ fontSize: 12 }}>
                {isPackage ? `Jaktpaket (${n} jakter) — totalt att erlägga:` : 'Gästjaktsavgift att erlägga:'}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'Arial, sans-serif' }}>{Math.round(amount).toLocaleString('sv-SE')} kr</div>
            </div>
            {isPackage && pricePerHunt && pricePerHunt > 0 && (
              <div style={{ fontSize: 10, color: '#666', fontFamily: 'Arial, sans-serif', marginBottom: 6 }}>
                Fördelat: {Math.round(pricePerHunt).toLocaleString('sv-SE')} kr / jakt × {n} jakttillfällen
              </div>
            )}
            <div style={{ fontSize: 10, color: '#777', fontFamily: 'Arial, sans-serif', lineHeight: 1.55 }}>
              Betalning sker med kort via Qvenari (Stripe) i samband med signering av detta avtal. Qvenari betalar ut avgiften till Upplåtaren efter avdrag för plattformsavgift. Avtalet träder i kraft när betalning har bekräftats. Kvitto tillhandahålls inom 48 timmar.
              {isPackage ? ' Priset inkluderar samtliga jakttillfällen i paketet.' : ''}
            </div>
          </div>
        </section>
      )}

      {/* ── Allmänna bestämmelser ── */}
      <section style={{ marginBottom: 22 }}>
        <div style={S.sectionHeading}>ALLMÄNNA BESTÄMMELSER</div>
        {[
          { par: 'A', title: 'Tillämplig lag', text: 'Detta avtal regleras av svensk rätt. Parterna förbinder sig att följa Jaktlagen (1987:259), Jaktförordningen (1987:905), Vapenlagen (1996:67) samt föreskrifter från Naturvårdsverket och länsstyrelsen.' },
          { par: 'B', title: 'Etik och uppförande', text: 'Deltagaren förbinder sig att under jakten uppträda på ett etiskt, respektfullt och ansvarsfullt sätt gentemot Jaktledaren, övriga deltagare, markägare och allmänhet. Diskriminering, rasism, sexism eller annat kränkande uppförande utgör grund för omedelbar utvisning från jakten utan återbetalningsrätt.' },
          { par: 'C', title: 'Marknadsplatsens roll', text: 'venari är en digital marknadsplats som tillhandahåller infrastruktur för kontaktskapande, avtalshantering och betalningsförmedling. Marknadsplatsen bär inget ansvar för avtalets fullgörande mellan parterna och är ej part i detta avtal.' },
          { par: 'D', title: 'Force majeure', text: 'Part är befriad från fullgörande av sina förpliktelser i den mån detta hindras av omständighet utanför parts rimliga kontroll, exempelvis extrema väderförhållanden, myndighetsbeslut om jaktstopp, pandemi eller annan allvarlig händelse. Upplåtaren äger rätt att föreslå nytt jakttillfälle som ersättning.' },
          { par: 'E', title: 'Ändringar', text: 'Ändringar av eller tillägg till detta avtal kräver skriftlig överenskommelse signerad av båda parter för att äga giltighet.' },
          { par: 'F', title: 'Tvistlösning', text: 'Tvist angående tolkning eller tillämpning av detta avtal ska i första hand lösas genom förhandling. Kan parterna inte nå enighet ska tvisten avgöras av allmän domstol med tingsrätten i det härad där Jaktmarken är belägen som första instans.' },
        ].map(({ par, title, text }) => (
          <div key={par} style={{ marginBottom: 8, display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 11 }}>
            <div style={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#888', minWidth: 16 }}>{par}.</div>
            <div style={{ flex: 1, color: '#333', lineHeight: 1.65 }}><span style={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#1a1a1a' }}>{title}. </span>{text}</div>
          </div>
        ))}
      </section>

      {/* ── Underskrifter ── */}
      <section style={{ borderTop: '2px solid #1a1a1a', paddingTop: 18 }}>
        <div style={{ fontSize: 10, fontFamily: 'Arial, sans-serif', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' as const, color: '#555', marginBottom: 8 }}>UNDERSKRIFTER</div>
        <div style={{ fontSize: 11, color: '#555', marginBottom: 18, fontStyle: 'italic' }}>
          Parterna intygar att de tagit del av och accepterar samtliga villkor i detta avtal.
          Avtalet är juridiskt bindande från och med Deltagarens underskrift och bekräftad betalning.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
          <div>
            <div style={{ fontSize: 9, fontFamily: 'Arial, sans-serif', color: '#999', textTransform: 'uppercase' as const, letterSpacing: '0.12em', marginBottom: 24 }}>Part 1 — Upplåtaren</div>
            <div style={{ borderTop: '1px solid #888', paddingTop: 5 }}>
              <div style={{ fontSize: 9, color: '#888', fontFamily: 'Arial, sans-serif' }}>Underskrift &amp; namnförtydligande</div>
              <div style={{ fontSize: 9, color: '#aaa', fontFamily: 'Arial, sans-serif', marginTop: 4 }}>Ort och datum</div>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 9, fontFamily: 'Arial, sans-serif', color: '#999', textTransform: 'uppercase' as const, letterSpacing: '0.12em', marginBottom: 8 }}>Part 2 — Deltagaren</div>
            {signatureName ? (
              <div style={{ background: '#f0faf0', border: '1px solid #4CAF50', borderRadius: 4, padding: '8px 12px', marginTop: 4 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Georgia, serif', letterSpacing: '0.03em' }}>{signatureName}</div>
                <div style={{ fontSize: 9, color: '#4CAF50', fontFamily: 'Arial, sans-serif', marginTop: 3 }}>✓ Elektronisk signatur</div>
                {signedAt && <div style={{ fontSize: 9, color: '#666', fontFamily: 'Arial, sans-serif', marginTop: 1 }}>{new Date(signedAt).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })}</div>}
              </div>
            ) : showSignaturePlaceholder ? (
              <div style={{ borderTop: '1px solid #888', paddingTop: 5, marginTop: 20 }}>
                <div style={{ fontSize: 9, color: '#888', fontFamily: 'Arial, sans-serif' }}>Underskrift &amp; namnförtydligande</div>
                <div style={{ fontSize: 9, color: '#aaa', fontFamily: 'Arial, sans-serif', marginTop: 4 }}>Ort och datum</div>
              </div>
            ) : null}
          </div>
        </div>
        <div style={{ marginTop: 22, paddingTop: 10, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', fontSize: 8, fontFamily: 'Arial, sans-serif', color: '#bbb', letterSpacing: '0.05em' }}>
          <span>venari · qvenari.com · Digital marknadsplats för jakt</span>
          <span>Ref: {refId}</span>
          <span>Upprättat elektroniskt via marknadsplatsen</span>
        </div>
      </section>
    </div>
    </div>
  );
}

// ── PUBLICERINGSAVTAL — venari ↔ Markägare ─────────────────────
export function PubliceringsAvtal({
  markName, ownerName, ownerEmail, orgNr, areaHa,
  signatureName, signedAt, contractRef, createdAt, showSignaturePlaceholder,
}: {
  markName?: string; ownerName?: string; ownerEmail?: string; orgNr?: string; areaHa?: number;
  signatureName?: string; signedAt?: string; contractRef?: string; createdAt?: string;
  showSignaturePlaceholder?: boolean;
}) {
  const today = new Date().toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });
  const refId = contractRef || 'QV-PUB-DRAFT';
  const dateStr = createdAt ? new Date(createdAt).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' }) : today;

  const docRef3 = React.useRef<HTMLDivElement>(null);
  const S = {
    doc: { background: '#ffffff', color: '#1a1a1a', fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 1.65, padding: '28px 32px', border: '1px solid #ccc', borderRadius: 3, margin: '0 auto', boxShadow: '0 3px 12px rgba(0,0,0,0.15)' } as React.CSSProperties,
    heading: { fontSize: 10, fontFamily: 'Arial, sans-serif', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' as const, color: '#555', borderBottom: '1.5px solid #c8c4bc', paddingBottom: 4, marginBottom: 12 } as React.CSSProperties,
    para: { marginBottom: 8, display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 11 } as React.CSSProperties,
    parNum: { fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#888', minWidth: 20, marginTop: 1 } as React.CSSProperties,
    parBody: { flex: 1, color: '#333', lineHeight: 1.65 } as React.CSSProperties,
    parTitle: { fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#1a1a1a' } as React.CSSProperties,
    defRow: { display: 'flex', gap: 10, marginBottom: 6, fontSize: 11 } as React.CSSProperties,
    defTerm: { fontFamily: 'Arial, sans-serif', fontWeight: 700, minWidth: 140, color: '#333' } as React.CSSProperties,
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button onClick={() => downloadAsPDF(docRef3, `Publiceringsavtal-${refId}.pdf`)} style={{ background: '#EE8F09', color: '#fff', border: 'none', borderRadius: 5, padding: '8px 16px', fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>⬇</span> Ladda ner PDF
        </button>
      </div>
    <div ref={docRef3} style={S.doc}>
      {/* ── Dokumenthuvud ── */}
      <div style={{ textAlign: 'center', borderBottom: '3px double #1a1a1a', paddingBottom: 18, marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
          <img src="/brand/deer-icon.png" alt="" style={{ width: 22, height: 22, opacity: 0.7 }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span style={{ fontSize: 9, fontFamily: 'Arial, sans-serif', letterSpacing: '0.3em', textTransform: 'uppercase' as const, color: '#888' }}>venari · Jakt &amp; Viltvård · Marknadsplats</span>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, marginBottom: 5 }}>PUBLICERINGSAVTAL</div>
        <div style={{ fontSize: 11, color: '#555', fontFamily: 'Arial, sans-serif', marginBottom: 6 }}>Avtal om publicering och förmedling av jakträtter via marknadsplatsen venari</div>
        <div style={{ fontSize: 10, color: '#666', fontFamily: 'Arial, sans-serif', letterSpacing: '0.05em' }}>
          Avtalsreferens: <strong>{refId}</strong> &nbsp;·&nbsp; Upprättat: {dateStr}
        </div>
      </div>

      {/* ── Avtalsparter ── */}
      <section style={{ marginBottom: 22 }}>
        <div style={S.heading}>AVTALSPARTER</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 12 }}>
          <div style={{ borderLeft: '3px solid #EE8F09', paddingLeft: 12 }}>
            <div style={{ fontSize: 8, fontFamily: 'Arial, sans-serif', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.12em', color: '#999', marginBottom: 5 }}>Part 1 — Markägaren</div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{ownerName || signatureName || '—'}</div>
            {ownerEmail && <div style={{ color: '#555', fontSize: 10, fontFamily: 'Arial, sans-serif' }}>{ownerEmail}</div>}
            {orgNr && <div style={{ color: '#666', fontSize: 10, fontFamily: 'Arial, sans-serif' }}>Org./Pers.nr: {orgNr}</div>}
            {markName && <div style={{ color: '#444', fontSize: 10, fontFamily: 'Arial, sans-serif', marginTop: 4 }}>Avseende: <em>{markName}</em>{areaHa ? ` (${areaHa} ha)` : ''}</div>}
            <div style={{ color: '#666', fontSize: 10, fontFamily: 'Arial, sans-serif', marginTop: 4, fontStyle: 'italic' }}>Nedan benämnd "Markägaren"</div>
          </div>
          <div style={{ borderLeft: '3px solid #4E594C', paddingLeft: 12 }}>
            <div style={{ fontSize: 8, fontFamily: 'Arial, sans-serif', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.12em', color: '#999', marginBottom: 5 }}>Part 2 — Marknadsplatsen</div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>venari</div>
            <div style={{ color: '#555', fontSize: 10, fontFamily: 'Arial, sans-serif' }}>qvenari.com</div>
            <div style={{ color: '#555', fontSize: 10, fontFamily: 'Arial, sans-serif' }}>kontakt@qvenari.com</div>
            <div style={{ color: '#444', fontSize: 10, fontFamily: 'Arial, sans-serif', marginTop: 4 }}>Digital marknadsplats för jakträtter och viltvård</div>
            <div style={{ color: '#666', fontSize: 10, fontFamily: 'Arial, sans-serif', marginTop: 4, fontStyle: 'italic' }}>Nedan benämnd "venari"</div>
          </div>
        </div>
        <div style={{ marginTop: 14, padding: '10px 14px', background: '#faf8f5', border: '1px solid #e8e4dc', borderRadius: 3, fontSize: 11, color: '#555', fontFamily: 'Arial, sans-serif', lineHeight: 1.6 }}>
          Parterna har ingått följande avtal om rätten för venari att publicera Markägarens jaktobjekt på marknadsplatsen och förmedla jakträtter och jakter till intresserade jägare. venari agerar som digital marknadsplats och är inte part i de enskilda jakträttsavtalen som ingås mellan Markägaren och slutkunder.
        </div>
      </section>

      {/* ── Definitioner ── */}
      <section style={{ marginBottom: 20 }}>
        <div style={S.heading}>DEFINITIONER</div>
        {[
          { term: 'Jaktmarken', def: `Den fastighet eller det markområde som benämns "${markName || 'angiven jaktmark'}" och som avses i detta avtal${areaHa ? ` (ca ${areaHa} ha)` : ''}.` },
          { term: 'Plattformen', def: 'Den digitala marknadsplatsen venari (qvenari.com) inklusive tillhörande applikationer, API:er och tjänster.' },
          { term: 'Annons', def: 'Den presentation av Jaktmarken, inklusive beskrivning, bilder, pris och villkor, som publiceras på Plattformen.' },
          { term: 'Transaktion', def: 'Varje genomförd försäljning av jakträtt, jakttillfälle eller jakttjänst via Plattformen avseende Jaktmarken.' },
          { term: 'Nettointäkt', def: 'Det belopp slutkunden betalar för Transaktionen, exklusive eventuell mervärdesskatt.' },
          { term: 'Förmedlingsarvode', def: 'Den ersättning om 20 % av Nettointäkten som venari erhåller för varje genomförd Transaktion.' },
          { term: 'Utbetalningsunderlag', def: 'Månatlig sammanställning från venari som redovisar genomförda Transaktioner, Förmedlingsarvode och Markägarens utbetalningsbelopp (80 % av Nettointäkten).' },
        ].map(({ term, def }) => (
          <div key={term} style={S.defRow}>
            <div style={S.defTerm}>{term}:</div>
            <div style={{ color: '#444', flex: 1, fontSize: 11 }}>{def}</div>
          </div>
        ))}
      </section>

      {/* ── Avtalets omfattning och rättigheter ── */}
      <section style={{ marginBottom: 20 }}>
        <div style={S.heading}>AVTALETS OMFATTNING OCH RÄTTIGHETER</div>
        {[
          { num: '§ 1', title: 'Publiceringsrätt', text: 'Markägaren ger venari en icke-exklusiv rätt att publicera Jaktmarken på Plattformen under avtalstiden. venari äger rätt att presentera, marknadsföra och exponera Annonsen för potentiella slutkunder i Sverige och internationellt.' },
          { num: '§ 2', title: 'Förmedlingsrätt', text: 'venari ges rätt att, för Markägarens räkning, ta emot ansökningar och förfrågningar om jakträtt och jakter avseende Jaktmarken. Godkännande av slutlig jakträttsinnehavare sker av Markägaren eller av Markägaren utsedd jaktledare. Det enskilda jakträttsavtalet ingås direkt mellan Markägaren och jakträttsinnehavaren.' },
          { num: '§ 3', title: 'Betalningsförmedling', text: 'venari hanterar betalningsflödet för Transaktioner via Plattformens betalningslösningar. venari innehåller Förmedlingsarvodet (20 % av Nettointäkten) och vidarebefordrar resterande 80 % till Markägaren i enlighet med betalningsvillkoren i § 6.' },
          { num: '§ 4', title: 'Dokumentation och verktyg', text: 'venari tillhandahåller digitala verktyg för hantering av ansökningar, kontraktsmallar, avskjutningsregistrering, gruppchat, kartfunktionalitet och statistik. Markägaren äger fullt förfogande över all data som lagras avseende Jaktmarken.' },
          { num: '§ 5', title: 'Markägarens skyldigheter', text: 'Markägaren ansvarar för att: (a) den information som lämnas i Annonsen är korrekt och fullständig; (b) nödvändiga jakträttsliga tillstånd och markrättigheter föreligger; (c) säkerhet och viltvård på Jaktmarken uppfyller gällande lagstiftning; (d) slutkunder behandlas i enlighet med ingångna jakträttsavtal.' },
        ].map(({ num, title, text }) => (
          <div key={num} style={S.para}>
            <div style={S.parNum}>{num}</div>
            <div style={S.parBody}><span style={S.parTitle}>{title}. </span>{text}</div>
          </div>
        ))}
      </section>

      {/* ── Ersättning och betalningsvillkor ── */}
      <section style={{ marginBottom: 20 }}>
        <div style={S.heading}>ERSÄTTNING OCH BETALNINGSVILLKOR</div>
        <div style={{ background: '#faf8f5', border: '1px solid #e0dbd0', borderRadius: 3, padding: '14px 18px', marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, fontSize: 12 }}>
            <div>
              <div style={{ fontSize: 9, fontFamily: 'Arial, sans-serif', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.1em', color: '#999', marginBottom: 6 }}>Förmedlingsarvode (venari)</div>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Arial, sans-serif', color: '#4E594C' }}>20 %</div>
              <div style={{ fontSize: 10, color: '#777', fontFamily: 'Arial, sans-serif', marginTop: 2 }}>av varje Transaktion (nettointäkt)</div>
            </div>
            <div>
              <div style={{ fontSize: 9, fontFamily: 'Arial, sans-serif', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.1em', color: '#999', marginBottom: 6 }}>Utbetalning till Markägaren</div>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Arial, sans-serif', color: '#EE8F09' }}>80 %</div>
              <div style={{ fontSize: 10, color: '#777', fontFamily: 'Arial, sans-serif', marginTop: 2 }}>av varje Transaktion (nettointäkt)</div>
            </div>
          </div>
        </div>
        {[
          { num: '§ 6', title: 'Utbetalningsprocess', text: 'venari sammanställer löpande Transaktioner under innevarande månad. Efter månadsskiftet upprättar venari ett Utbetalningsunderlag som sänds till Markägaren senast den 10:e i efterföljande månad. Underlaget redovisar samtliga genomförda Transaktioner, tillhörande Förmedlingsarvoden och det nettobelopp som tillkommer Markägaren.' },
          { num: '§ 7', title: 'Betalningsvillkor', text: 'Utbetalning av Markägarens andel (80 % av Nettointäkten) sker mot faktura med 30 dagars betalningstid från fakturadatum. Markägaren är ansvarig för att tillhandahålla korrekta faktureringsuppgifter, inklusive organisationsnummer eller personnummer, faktura-e-postadress och bankgiro eller kontonummer för utbetalning.' },
          { num: '§ 8', title: 'Mervärdesskatt', text: 'Förmedlingsarvodet är angivet exklusive mervärdesskatt. Om venari är momsregistrerat tillkommer lagstadgad mervärdesskatt på Förmedlingsarvodet. Markägaren ansvarar för sin egen momshantering avseende de intäkter som tillfaller Markägaren.' },
          { num: '§ 9', title: 'Kostnadsfri publicering', text: 'Publicering på Plattformen är kostnadsfri för Markägaren. Ersättning till venari utgår enbart i form av Förmedlingsarvodet vid genomförda Transaktioner — inga fasta avgifter, listningsavgifter eller abonnemangskostnader debiteras.' },
        ].map(({ num, title, text }) => (
          <div key={num} style={S.para}>
            <div style={S.parNum}>{num}</div>
            <div style={S.parBody}><span style={S.parTitle}>{title}. </span>{text}</div>
          </div>
        ))}
      </section>

      {/* ── Dataskydd och sekretess ── */}
      <section style={{ marginBottom: 20 }}>
        <div style={S.heading}>DATASKYDD OCH SEKRETESS</div>
        {[
          { num: '§ 10', title: 'Personuppgifter', text: 'venari behandlar personuppgifter för Markägaren och slutkunder i enlighet med Dataskyddsförordningen (GDPR, EU 2016/679) och tillämplig nationell dataskyddslagstiftning. venari är personuppgiftsansvarig för de uppgifter som samlas in via Plattformen och behandlar dessa enbart för att uppfylla avtalets ändamål.' },
          { num: '§ 11', title: 'Sekretess', text: 'Parterna förbinder sig att inte röja konfidentiell information om den andra parten till tredje man utan föregående skriftligt samtycke. Sekretesskyldigheten gäller under avtalstiden och tre (3) år därefter. Sekretess gäller inte för information som är allmänt känd eller som part är skyldig att lämna ut enligt lag.' },
          { num: '§ 12', title: 'Dataportabilitet', text: 'Markägaren äger full rätt att när som helst exportera all sin data från Plattformen. venari ska på begäran tillhandahålla all data hänförlig till Markägarens jaktmark i ett maskinläsbart format inom tio (10) arbetsdagar.' },
        ].map(({ num, title, text }) => (
          <div key={num} style={S.para}>
            <div style={S.parNum}>{num}</div>
            <div style={S.parBody}><span style={S.parTitle}>{title}. </span>{text}</div>
          </div>
        ))}
      </section>

      {/* ── Avtalstid och uppsägning ── */}
      <section style={{ marginBottom: 20 }}>
        <div style={S.heading}>AVTALSTID OCH UPPSÄGNING</div>
        {[
          { num: '§ 13', title: 'Avtalstid', text: 'Avtalet gäller tillsvidare från och med signeringsdatum. Avtalet förnyas automatiskt för varje löpande period om inte någon av parterna dessförinnan säger upp det.' },
          { num: '§ 14', title: 'Uppsägning', text: 'Endera parten kan säga upp avtalet med en (1) månads skriftlig uppsägningstid. Uppsägning sker via e-post till den andra partens registrerade e-postadress. Under uppsägningstiden gäller samtliga avtalets villkor fullt ut. Pågående Transaktioner och jakträttsavtal som ingåtts före uppsägningens verkan berörs inte av uppsägningen.' },
          { num: '§ 15', title: 'Omedelbar hävning', text: 'venari äger rätt att med omedelbar verkan häva avtalet och avpublicera Annonsen om Markägaren: (a) lämnar vilseledande uppgifter i Annonsen; (b) upprepade gånger brister i sina skyldigheter mot slutkunder; (c) bryter mot tillämplig jaktlagstiftning eller säkerhetsföreskrifter; eller (d) på annat sätt allvarligt skadar venaris varumärke eller slutkunders intressen.' },
        ].map(({ num, title, text }) => (
          <div key={num} style={S.para}>
            <div style={S.parNum}>{num}</div>
            <div style={S.parBody}><span style={S.parTitle}>{title}. </span>{text}</div>
          </div>
        ))}
      </section>

      {/* ── Allmänna bestämmelser ── */}
      <section style={{ marginBottom: 22 }}>
        <div style={S.heading}>ALLMÄNNA BESTÄMMELSER</div>
        {[
          { par: 'A', title: 'Tillämplig lag', text: 'Avtalet tolkas och regleras av svensk rätt. Distansavtalslagen (2005:59), Avtalslagen (1915:218) och Lagen om elektronisk handel (2002:562) äger tillämpning i relevanta delar.' },
          { par: 'B', title: 'Ansvarsbegränsning', text: "venari ansvarar inte för indirekta förluster, utebliven vinst eller följdskador. venari's totala ansvar under avtalet är begränsat till det Förmedlingsarvode som betalats av Markägaren under de senaste tolv (12) månaderna." },
          { par: 'C', title: 'Force majeure', text: 'Part är befriad från fullgörande av sina förpliktelser i den mån detta hindras av omständighet utanför partens rimliga kontroll, såsom naturkatastrof, krig, pandemi, lagstiftning eller myndighetsbeslut.' },
          { par: 'D', title: 'Ändringar i villkoren', text: 'venari förbehåller sig rätten att uppdatera dessa villkor med trettio (30) dagars föregående skriftligt meddelande till Markägaren. Fortsatt användning av Plattformen efter meddelandeperiodens utgång utgör godkännande av de ändrade villkoren.' },
          { par: 'E', title: 'Överlåtelse', text: 'Markägaren får inte överlåta sina rättigheter eller skyldigheter enligt detta avtal utan venaris skriftliga godkännande. venari äger rätt att överlåta avtalet i samband med företagsöverlåtelse eller omstrukturering, förutsatt att slutkunders och Markägarens rättigheter inte påverkas negativt.' },
          { par: 'F', title: 'Tvistlösning', text: 'Tvist angående tolkning eller tillämpning av detta avtal skall i första hand lösas genom förhandling. Kan parterna inte nå enighet inom trettio (30) dagar skall tvisten avgöras av allmän domstol med Stockholm tingsrätt som första instans.' },
          { par: 'G', title: 'Hela avtalet', text: 'Detta dokument utgör det fullständiga avtalet mellan parterna avseende publicering och förmedling av jakträtter. Tidigare muntliga eller skriftliga överenskommelser i samma ämne ersätts av detta avtal.' },
        ].map(({ par, title, text }) => (
          <div key={par} style={S.para}>
            <div style={S.parNum}>{par}.</div>
            <div style={S.parBody}><span style={S.parTitle}>{title}. </span>{text}</div>
          </div>
        ))}
      </section>

      {/* ── Underskrifter ── */}
      <section style={{ borderTop: '2px solid #1a1a1a', paddingTop: 18 }}>
        <div style={{ fontSize: 10, fontFamily: 'Arial, sans-serif', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' as const, color: '#555', marginBottom: 8 }}>UNDERSKRIFTER</div>
        <div style={{ fontSize: 11, color: '#555', marginBottom: 18, fontStyle: 'italic' }}>
          Parterna intygar att de tagit del av och accepterar samtliga villkor i detta avtal. Avtalet är juridiskt bindande från och med Markägarens elektroniska underskrift.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
          <div>
            <div style={{ fontSize: 9, fontFamily: 'Arial, sans-serif', color: '#999', textTransform: 'uppercase' as const, letterSpacing: '0.12em', marginBottom: 8 }}>Part 1 — Markägaren</div>
            {signatureName ? (
              <div style={{ background: '#f0faf0', border: '1px solid #4CAF50', borderRadius: 4, padding: '8px 12px', marginTop: 4 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Georgia, serif', letterSpacing: '0.03em' }}>{signatureName}</div>
                <div style={{ fontSize: 9, color: '#4CAF50', fontFamily: 'Arial, sans-serif', marginTop: 3 }}>✓ Elektronisk signatur</div>
                {signedAt && <div style={{ fontSize: 9, color: '#666', fontFamily: 'Arial, sans-serif', marginTop: 1 }}>{new Date(signedAt).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })}</div>}
              </div>
            ) : showSignaturePlaceholder ? (
              <div style={{ borderTop: '1px solid #888', paddingTop: 5, marginTop: 20 }}>
                <div style={{ fontSize: 9, color: '#888', fontFamily: 'Arial, sans-serif' }}>Underskrift &amp; namnförtydligande</div>
                <div style={{ fontSize: 9, color: '#aaa', fontFamily: 'Arial, sans-serif', marginTop: 4 }}>Ort och datum</div>
              </div>
            ) : null}
          </div>
          <div>
            <div style={{ fontSize: 9, fontFamily: 'Arial, sans-serif', color: '#999', textTransform: 'uppercase' as const, letterSpacing: '0.12em', marginBottom: 8 }}>Part 2 — venari</div>
            <div style={{ background: '#f0faf0', border: '1px solid #4CAF50', borderRadius: 4, padding: '8px 12px', marginTop: 4 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Georgia, serif', letterSpacing: '0.03em' }}>venari</div>
              <div style={{ fontSize: 9, color: '#4CAF50', fontFamily: 'Arial, sans-serif', marginTop: 3 }}>✓ Elektronisk signatur vid publicering</div>
              <div style={{ fontSize: 9, color: '#666', fontFamily: 'Arial, sans-serif', marginTop: 1 }}>{dateStr}</div>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 22, paddingTop: 10, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', fontSize: 8, fontFamily: 'Arial, sans-serif', color: '#bbb', letterSpacing: '0.05em' }}>
          <span>venari · qvenari.com · Digital marknadsplats för jakt</span>
          <span>Ref: {refId}</span>
          <span>Upprättat elektroniskt via marknadsplatsen</span>
        </div>
      </section>
    </div>
    </div>
  );
}

// ── PUBLICERINGSAVTAL MODAL ─────────────────────────────────────
export function PubliceringsAvtalModal({
  markName, ownerName, ownerEmail, orgNr, areaHa, onClose, onSigned,
}: {
  markName?: string; ownerName?: string; ownerEmail?: string; orgNr?: string; areaHa?: number;
  onClose: () => void; onSigned?: (name: string) => void;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [step, setStep] = useState<'read' | 'sign' | 'done'>('read');
  const [signName, setSignName] = useState('');
  const [signedAt, setSignedAt] = useState<string | undefined>();
  const refId = `QV-PUB-${Date.now().toString(36).toUpperCase()}`;

  function doSign() {
    if (!signName.trim()) return;
    const now = new Date().toISOString();
    setSignedAt(now);
    setStep('done');
    onSigned?.(signName.trim());
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, width: '100%', maxWidth: 720, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '16px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: T.text, fontWeight: 700, fontSize: 16 }}>Publiceringsavtal</div>
            <div style={{ color: T.textSub, fontSize: 12, marginTop: 2 }}>venari ↔ Markägare · Ref: {refId}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 22, lineHeight: 1 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
          <PubliceringsAvtal
            markName={markName} ownerName={ownerName} ownerEmail={ownerEmail}
            orgNr={orgNr} areaHa={areaHa}
            contractRef={refId}
            signatureName={step === 'done' ? signName : undefined}
            signedAt={step === 'done' ? signedAt : undefined}
            showSignaturePlaceholder={step !== 'done'}
          />
        </div>

        {/* Footer / actions */}
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${T.border}`, background: T.surface }}>
          {step === 'read' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: T.textSub, fontSize: 12 }}>Läs igenom avtalet ovan innan du signerar.</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={onClose} style={{ padding: '9px 20px', background: 'none', border: `1px solid ${T.border}`, borderRadius: 6, color: T.textSub, cursor: 'pointer', fontSize: 13 }}>Avbryt</button>
                <button onClick={() => setStep('sign')} style={{ padding: '9px 22px', background: '#EE8F09', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Gå vidare till signering</button>
              </div>
            </div>
          )}
          {step === 'sign' && (
            <div>
              <div style={{ color: T.text, fontSize: 13, marginBottom: 10 }}>Bekräfta din identitet genom att skriva ditt fullständiga namn:</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  value={signName}
                  onChange={e => setSignName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && doSign()}
                  placeholder="Förnamn Efternamn"
                  style={{ flex: 1, padding: '9px 14px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, fontSize: 14 }}
                  autoFocus
                />
                <button onClick={doSign} disabled={!signName.trim()} style={{ padding: '9px 22px', background: signName.trim() ? '#4CAF50' : T.surface, border: 'none', borderRadius: 6, color: signName.trim() ? '#fff' : T.textSub, cursor: signName.trim() ? 'pointer' : 'default', fontWeight: 700, fontSize: 13 }}>
                  Signera avtal
                </button>
              </div>
              <div style={{ color: T.textSub, fontSize: 11, marginTop: 8 }}>Genom att signera bekräftar du att du har befogenhet att ingå avtalet för Markägarens räkning och att du accepterar samtliga villkor.</div>
            </div>
          )}
          {step === 'done' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: '#4CAF50', fontSize: 14, fontWeight: 700 }}>✓ Avtalet är signerat och gäller från och med nu.</div>
              <button onClick={onClose} style={{ padding: '9px 24px', background: '#EE8F09', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Stäng</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── CONTRACT MODAL (sign + Stripe) ──────────────────────────────
function ContractModal({ contract, mark, onClose, onUpdate }: { contract: ApplicationContract; mark: Mark; onClose: () => void; onUpdate: () => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [signName, setSignName] = useState('');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<'contract' | 'pay'>(
    contract.status === 'signed' && contract.paymentStatus === 'unpaid' ? 'pay' : 'contract'
  );
  const [markAmount, setMarkAmount] = useState<number>(0);

  // Markens standardavgift används om avtalet saknar belopp
  useEffect(() => {
    if (!contract.amount && mark?.id) {
      gql(`query M($id: ID!) { mark(id: $id) { swishAmount } }`, { id: mark.id })
        .then(d => { if (d.mark) setMarkAmount(d.mark.swishAmount || 0); })
        .catch(() => {});
    }
  }, [contract.amount, mark?.id]);

  const effectiveAmount = contract.amount || markAmount;

  async function signit() {
    if (!signName.trim()) return;
    setBusy(true);
    try {
      await gql(`mutation SC($id: ID!, $name: String!) { signApplicationContract(contractId: $id, signatureName: $name) { id status } }`, { id: contract.id, name: signName });
      setStep('pay');
    } catch (e: any) { alert(e.message); }
    setBusy(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 28, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ color: T.text, margin: 0, fontSize: 18 }}>{step === 'contract' ? 'Kontrakt' : 'Betalning'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 20 }}>x</button>
        </div>

        {step === 'contract' ? (
          <>
            <div style={{ marginBottom: 20 }}>
              <LegalContractDocument
                contractText={contract.contractText}
                markName={mark.name}
                amount={contract.amount}
                signatureName={contract.signatureName || undefined}
                signedAt={contract.signedAt || undefined}
                applicantEmail={contract.applicantEmail}
                contractRef={`QV-${contract.id.slice(-6).toUpperCase()}`}
                createdAt={contract.createdAt}
                showSignaturePlaceholder={contract.status === 'pending_signature'}
              />
            </div>

            {contract.status === 'pending_signature' ? (
              <>
                <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 16, marginBottom: 16 }}>
                  <div style={{ color: T.textSub, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, marginBottom: 8 }}>Signera avtalet med ditt fullständiga namn</div>
                  <input value={signName} onChange={e => setSignName(e.target.value)} placeholder="Förnamn Efternamn" style={{ width: '100%', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 4, padding: '12px 14px', color: T.text, fontSize: 16, outline: 'none', boxSizing: 'border-box' as const }} />
                  <div style={{ color: T.textMuted, fontSize: 11, marginTop: 8 }}>Genom att signera bekräftar du att du tagit del av och godkänner samtliga villkor i avtalet, samt förbinder dig att erlägga angivet belopp.</div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button onClick={onClose} style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 5, padding: '12px 20px', cursor: 'pointer', flex: 1 }}>Avbryt</button>
                  <button onClick={signit} disabled={busy || !signName.trim()} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 5, padding: '12px 20px', fontWeight: 700, cursor: 'pointer', flex: 2 }}>{busy ? 'Signerar...' : 'Signera avtal'}</button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <div style={{ color: '#4CAF50', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>✓ Avtal signerat av {contract.signatureName}</div>
                <div style={{ color: T.textSub, fontSize: 12 }}>{contract.signedAt ? new Date(contract.signedAt).toLocaleDateString('sv-SE') : ''}</div>
                {contract.paymentStatus === 'unpaid' && (
                  <button onClick={() => setStep('pay')} style={{ background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 5, padding: '12px 24px', fontWeight: 700, cursor: 'pointer', marginTop: 16 }}>Betala med kort</button>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <ContractPayButton contractId={contract.id} amount={effectiveAmount} />
          </>
        )}
      </div>
    </div>
  );
}

// ── APPLICATIONS PANEL (for mark managers) ─────────────────────
export function ApplicationsPanel({ markId, userEmail, userRole }: { markId: string; userEmail: string; userRole: string }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [applications, setApplications] = useState<MarkApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [selected, setSelected] = useState<MarkApplication | null>(null);
  const [requirements, setRequirements] = useState<MarkRequirements | null>(null);
  const [showReqEditor, setShowReqEditor] = useState(false);
  const [dispensationTarget, setDispensationTarget] = useState('');
  const [dispensationMsg, setDispensationMsg] = useState('');
  const [dispensationBusy, setDispensationBusy] = useState(false);
  const [pendingDispensations, setPendingDispensations] = useState<DispensationRequest[]>([]);
  const [questionText, setQuestionText] = useState('');
  const [questionBusy, setQuestionBusy] = useState(false);
  const [contractText, setContractText] = useState('');
  const [contractAmount, setContractAmount] = useState(0);
  const [contractSwish, setContractSwish] = useState('');
  const [contractBusy, setContractBusy] = useState(false);
  const [showContractForm, setShowContractForm] = useState(false);
  const [showContractBuilder, setShowContractBuilder] = useState(false);

  // Pre-load mark's Swish settings for contract form
  useEffect(() => {
    gql(`query M($id: ID!) { mark(id: $id) { swishNumber swishAmount contractTemplate } }`, { id: markId })
      .then(d => {
        if (d.mark) {
          if (d.mark.swishNumber) setContractSwish(d.mark.swishNumber);
          if (d.mark.swishAmount) setContractAmount(d.mark.swishAmount);
          if (d.mark.contractTemplate) setContractText(d.mark.contractTemplate);
        }
      }).catch(() => {});
  }, [markId]);

  const canSetRequirements = ['markagare', 'jaktrattinnehavare', 'jaktledare', 'admin'].some(r =>
    userRole.toLowerCase().replace(/[aao]/g, m => m === 'a' ? 'a' : m === 'a' ? 'a' : 'o').replace(/[^a-z]/g, '') === r
  ) || userRole === 'jaktledare' || userRole === 'admin';

  const APP_FIELDS = `id markId applicantEmail applicantName applicationText videoUrl profileSnapshot meetsRequirements status reviewedBy reviewedAt reviewNote adminQuestion applicantResponse interestRoles dispensationRequest { id status requestedTo requestMessage decisionMessage } contract { id status signedAt signatureName ownerSignedAt ownerSignatureName amount swishNumber paymentStatus paymentReference contractText } createdAt`;

  const load = async () => {
    setLoading(true);
    try {
      const [appData, reqData, dispData] = await Promise.all([
        gql(`query Apps($markId: ID!, $status: String) { markApplications(markId: $markId, status: $status) { ${APP_FIELDS} } }`, { markId, status: filter === 'all' ? undefined : filter }),
        gql(`query Req($markId: ID!) { markRequirements(markId: $markId) { id minExperienceYears requiresWeaponLicense requiresDog minHunterYears afterhuntRequired customRequirements { label required } requirementsVisible delegatedReviewerRoles } }`, { markId }),
        gql(`query PD($markId: ID) { pendingDispensations(markId: $markId) { id applicationId requestedBy requestedTo requestMessage status createdAt } }`, { markId }),
      ]);
      setApplications(appData.markApplications || []);
      setRequirements(reqData.markRequirements);
      setPendingDispensations(dispData.pendingDispensations || []);
      // Load mark swish info
      // We'll load this when the contract form opens
    } catch (e: any) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [markId, filter]);

  const filtered = filter === 'all' ? applications : applications.filter(a => a.status === filter);

  async function approve(appId: string) {
    try {
      await gql(`mutation RA($id: ID!, $status: String!) { reviewApplication(applicationId: $id, status: $status) { id status } }`, { id: appId, status: 'approved' });
      await load();
    } catch (e: any) { alert(e.message); }
  }

  async function reject(appId: string, note?: string) {
    try {
      await gql(`mutation RA($id: ID!, $status: String!, $note: String) { reviewApplication(applicationId: $id, status: $status, reviewNote: $note) { id status } }`, { id: appId, status: 'rejected', note });
      await load();
    } catch (e: any) { alert(e.message); }
  }

  async function askQuestion(appId: string) {
    if (!questionText.trim()) return;
    setQuestionBusy(true);
    try {
      await gql(`mutation AQ($id: ID!, $q: String!) { askApplicationQuestion(applicationId: $id, question: $q) { id status } }`, { id: appId, q: questionText });
      setQuestionText('');
      await load();
      setSelected(null);
    } catch (e: any) { alert(e.message); }
    setQuestionBusy(false);
  }

  async function sendContract(appId: string) {
    if (!contractText.trim() || contractAmount <= 0) return;
    setContractBusy(true);
    try {
      await gql(`mutation CC($appId: ID!, $text: String!, $amount: Float!, $swish: String) { createApplicationContract(applicationId: $appId, contractText: $text, amount: $amount, swishNumber: $swish) { id status } }`, { appId, text: contractText, amount: contractAmount, swish: contractSwish || undefined });
      setContractText('');
      setContractAmount(0);
      setShowContractForm(false);
      await load();
      setSelected(null);
    } catch (e: any) { alert(e.message); }
    setContractBusy(false);
  }

  async function askDispensation(app: MarkApplication) {
    if (!dispensationTarget) return;
    setDispensationBusy(true);
    try {
      await gql(`mutation RD($appId: ID!, $to: String!, $msg: String) { requestDispensation(applicationId: $appId, requestedTo: $to, requestMessage: $msg) { id status } }`, { appId: app.id, to: dispensationTarget, msg: dispensationMsg || undefined });
      setDispensationTarget('');
      setDispensationMsg('');
      await load();
      setSelected(null);
    } catch (e: any) { alert(e.message); }
    setDispensationBusy(false);
  }

  async function respondDispensation(dispId: string, granted: boolean) {
    const msg = prompt(granted ? 'Meddelande (valfritt):' : 'Orsak till avslag:') || undefined;
    try {
      await gql(`mutation Resp($id: ID!, $granted: Boolean!, $msg: String) { respondToDispensation(dispensationId: $id, granted: $granted, decisionMessage: $msg) { id status } }`, { id: dispId, granted, msg });
      await load();
    } catch (e: any) { alert(e.message); }
  }

  const filterBtns = [
    { k: 'all', l: 'Alla' },
    { k: 'interest_registered', l: 'Intresse' },
    { k: 'user_signed', l: 'Väntar sign.' },
    { k: 'qualified', l: 'Kvalificerade' },
    { k: 'pending', l: 'Väntande' },
    { k: 'contract_sent', l: 'Kontrakt' },
    { k: 'completed', l: 'Klara' },
    { k: 'rejected', l: 'Avvisade' },
  ];

  const iS: React.CSSProperties = { width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '10px 14px', color: T.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'inherit' };
  const lS: React.CSSProperties = { display: 'block', color: T.textSub, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, marginBottom: 6 };

  return (
    <div style={{ padding: '0 0 24px' }}>
      {/* Pending dispensations inbox */}
      {pendingDispensations.length > 0 && (
        <div style={{ background: 'rgba(155,89,182,0.1)', border: '1px solid rgba(155,89,182,0.3)', borderRadius: 5, padding: 16, marginBottom: 20 }}>
          <div style={{ color: '#9b59b6', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, marginBottom: 12 }}>
            Dispensansokningar ({pendingDispensations.length})
          </div>
          {pendingDispensations.map(d => (
            <div key={d.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: 14, marginBottom: 8 }}>
              <div style={{ color: T.text, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Dispens begard av {d.requestedBy}</div>
              {d.requestMessage && <div style={{ color: T.textSub, fontSize: 12, marginBottom: 10 }}>{d.requestMessage}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => respondDispensation(d.id, true)} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 4, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Bevilja dispens</button>
                <button onClick={() => respondDispensation(d.id, false)} style={{ background: 'rgba(192,57,43,0.2)', color: '#c0392b', border: 'none', borderRadius: 4, padding: '8px 16px', fontSize: 12, cursor: 'pointer' }}>Neka</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Owner signatures section */}
      <div style={{ background: 'rgba(97,216,54,0.06)', border: '1px solid rgba(97,216,54,0.25)', borderRadius: 8, padding: 16, marginBottom: 20 }}>
        <div style={{ color: '#61D836', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, marginBottom: 12 }}>
          Väntar på din signatur
        </div>
        <OwnerSigningPanel markId={markId} />
      </div>

      {/* Requirements + filters header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' as const, gap: 8 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const }}>
          {filterBtns.map(f => (
            <button key={f.k} onClick={() => setFilter(f.k)} style={{ background: filter === f.k ? T.amber : T.surface, color: filter === f.k ? '#000' : T.text, border: `1px solid ${filter === f.k ? T.amber : T.border}`, borderRadius: 10, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{f.l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {canSetRequirements && <button onClick={() => setShowReqEditor(true)} style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 5, padding: '8px 16px', fontSize: 12, cursor: 'pointer' }}>Kravprofil</button>}
        </div>
      </div>

      {/* Requirements summary */}
      {requirements && (requirements.minExperienceYears > 0 || requirements.requiresWeaponLicense || requirements.requiresDog || requirements.afterhuntRequired || (requirements.customRequirements || []).filter((r: any) => r.required).length > 0) && (
        <div style={{ background: `${T.amber}08`, border: `1px solid ${T.amber}33`, borderRadius: 6, padding: 16, marginBottom: 18 }}>
          <div style={{ color: T.amber, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, marginBottom: 10 }}>Kravprofil</div>
          <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 8 }}>
            {requirements.minExperienceYears > 0 && <span style={{ background: `${T.amber}18`, color: T.amber, fontSize: 12, padding: '4px 12px', borderRadius: 10, border: `1px solid ${T.amber}44` }}>Min {requirements.minExperienceYears} ars erfarenhet</span>}
            {requirements.requiresWeaponLicense && <span style={{ background: `${T.amber}18`, color: T.amber, fontSize: 12, padding: '4px 12px', borderRadius: 10, border: `1px solid ${T.amber}44` }}>Vapenlicens</span>}
            {requirements.requiresDog && <span style={{ background: `${T.amber}18`, color: T.amber, fontSize: 12, padding: '4px 12px', borderRadius: 10, border: `1px solid ${T.amber}44` }}>Jakthund</span>}
            {requirements.afterhuntRequired && <span style={{ background: `${T.amber}18`, color: T.amber, fontSize: 12, padding: '4px 12px', borderRadius: 10, border: `1px solid ${T.amber}44` }}>Eftersoksutbildning</span>}
            {(requirements.customRequirements || []).filter((r: any) => r.required).map((r: any, i: number) => <span key={i} style={{ background: `${T.amber}18`, color: T.amber, fontSize: 12, padding: '4px 12px', borderRadius: 10, border: `1px solid ${T.amber}44` }}>{r.label}</span>)}
          </div>
        </div>
      )}

      {/* Application list */}
      {loading ? <div style={{ color: T.textSub, fontSize: 13, textAlign: 'center', padding: 40 }}>Laddar...</div> : (
        filtered.length === 0 ? (
          <div style={{ color: T.textMuted, fontSize: 13, textAlign: 'center', padding: 40 }}>Inga ansokningar</div>
        ) : (
          filtered.map(app => {
            const st = statusLabel(app.status);
            const profile = app.profileSnapshot ? (() => { try { return JSON.parse(app.profileSnapshot!); } catch { return null; } })() : null;
            return (
              <div key={app.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 5, padding: 16, marginBottom: 10, cursor: 'pointer' }} onClick={() => { setShowContractForm(false); setSelected(app); }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>{app.applicantName || app.applicantEmail}</div>
                    <div style={{ color: T.textSub, fontSize: 12, marginTop: 2 }}>{app.applicantEmail}</div>
                    {profile && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' as const }}>
                        {profile.experienceLevel && <span style={{ background: `${T.amber}22`, color: T.amber, fontSize: 10, padding: '2px 8px', borderRadius: 10 }}>{profile.experienceLevel}</span>}
                        {profile.dogHandler && <span style={{ background: '#61D83622', color: '#61D836', fontSize: 10, padding: '2px 8px', borderRadius: 10 }}>Hundforare</span>}
                        {profile.afterhuntCertified && <span style={{ background: 'rgba(0,162,255,0.15)', color: '#00A2FF', fontSize: 10, padding: '2px 8px', borderRadius: 10 }}>Eftersok</span>}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'flex-end', gap: 6 }}>
                    <span style={{ background: `${st.color}22`, color: st.color, fontSize: 11, padding: '3px 10px', borderRadius: 10, fontWeight: 600 }}>{st.label}</span>
                    {!app.meetsRequirements && app.status === 'pending' && <span style={{ background: 'rgba(192,57,43,0.15)', color: '#c0392b', fontSize: 10, padding: '2px 8px', borderRadius: 10 }}>Uppfyller ej krav</span>}
                    {app.contract && <span style={{ background: `${statusLabel(app.contract.paymentStatus === 'paid' ? 'completed' : app.contract.status).color}22`, color: statusLabel(app.contract.paymentStatus === 'paid' ? 'completed' : app.contract.status).color, fontSize: 10, padding: '2px 8px', borderRadius: 10 }}>{app.contract.paymentStatus === 'paid' ? 'Betald' : app.contract.status === 'signed' ? 'Signerat' : 'Kontrakt'}</span>}
                  </div>
                </div>
                {(app as any).interestRoles?.length > 0 && (
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' as const, marginTop: 8 }}>
                    {((app as any).interestRoles as string[]).map((r: string) => (
                      <span key={r} style={{ background: `${T.amber}18`, color: T.amber, borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 600 }}>
                        {INTEREST_ROLES.find(x => x.id === r)?.label || r}
                      </span>
                    ))}
                  </div>
                )}
                {app.applicationText && <div style={{ color: T.textSub, fontSize: 12, marginTop: 10, lineHeight: 1.6, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>{app.applicationText.slice(0, 180)}{app.applicationText.length > 180 ? '...' : ''}</div>}
                <div style={{ color: T.textMuted, fontSize: 11, marginTop: 8 }}>{new Date(app.createdAt).toLocaleDateString('sv-SE')}</div>
              </div>
            );
          })
        )
      )}

      {/* Application detail modal */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={e => e.target === e.currentTarget && setSelected(null)}>
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 28, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ color: T.text, margin: 0, fontSize: 18 }}>Ansokan — {selected.applicantName || selected.applicantEmail}</h3>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 20 }}>x</button>
            </div>

            {/* Status badge */}
            {(() => { const st = statusLabel(selected.status); return <div style={{ display: 'inline-block', background: `${st.color}22`, color: st.color, fontSize: 12, padding: '4px 12px', borderRadius: 10, fontWeight: 600, marginBottom: 10 }}>{st.label}</div>; })()}

            {/* Interest roles */}
            {(selected as any).interestRoles?.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, marginBottom: 14 }}>
                <span style={{ color: T.textMuted, fontSize: 11, marginRight: 2, alignSelf: 'center' }}>Roller:</span>
                {((selected as any).interestRoles as string[]).map((r: string) => (
                  <span key={r} style={{ background: `${T.amber}20`, color: T.amber, border: `1px solid ${T.amber}40`, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                    {INTEREST_ROLES.find(x => x.id === r)?.label || r}
                  </span>
                ))}
              </div>
            )}

            {/* Profile snapshot */}
            {selected.profileSnapshot && (() => {
              try {
                const p = JSON.parse(selected.profileSnapshot);
                return (
                  <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 5, padding: 14, marginBottom: 16 }}>
                    <div style={{ color: T.amber, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, marginBottom: 10 }}>Jagarprofil</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {p.experienceLevel && <div><span style={{ color: T.textMuted, fontSize: 11 }}>Niva: </span><span style={{ color: T.text, fontSize: 12 }}>{p.experienceLevel}</span></div>}
                      {p.hunterSinceYear && <div><span style={{ color: T.textMuted, fontSize: 11 }}>Jagare sedan: </span><span style={{ color: T.text, fontSize: 12 }}>{p.hunterSinceYear}</span></div>}
                      <div><span style={{ color: T.textMuted, fontSize: 11 }}>Hundforare: </span><span style={{ color: T.text, fontSize: 12 }}>{p.dogHandler ? 'Ja' : 'Nej'}</span></div>
                      <div><span style={{ color: T.textMuted, fontSize: 11 }}>Eftersok: </span><span style={{ color: T.text, fontSize: 12 }}>{p.afterhuntCertified ? 'Ja' : 'Nej'}</span></div>
                    </div>
                    {p.certifications?.length > 0 && <div style={{ marginTop: 8, color: T.textSub, fontSize: 12 }}>Certifieringar: {p.certifications.join(', ')}</div>}
                  </div>
                );
              } catch { return null; }
            })()}

            {/* Application text */}
            {selected.applicationText && (
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 5, padding: 14, marginBottom: 16 }}>
                <div style={{ color: T.amber, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, marginBottom: 8 }}>Presentation</div>
                <div style={{ color: T.text, fontSize: 13, lineHeight: 1.7 }}>{selected.applicationText}</div>
              </div>
            )}

            {/* Admin question / response */}
            {selected.adminQuestion && (
              <div style={{ background: 'rgba(0,162,255,0.08)', border: '1px solid rgba(0,162,255,0.25)', borderRadius: 5, padding: 14, marginBottom: 16 }}>
                <div style={{ color: '#00A2FF', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, marginBottom: 6 }}>Din fraga</div>
                <div style={{ color: T.text, fontSize: 13, marginBottom: selected.applicantResponse ? 10 : 0 }}>{selected.adminQuestion}</div>
                {selected.applicantResponse && (
                  <>
                    <div style={{ color: '#61D836', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, marginTop: 10, marginBottom: 4 }}>Svar</div>
                    <div style={{ color: T.text, fontSize: 13 }}>{selected.applicantResponse}</div>
                  </>
                )}
              </div>
            )}

            {/* Meets requirements badge */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {selected.meetsRequirements ? (
                <span style={{ background: '#61D83622', color: '#61D836', fontSize: 12, padding: '4px 12px', borderRadius: 10 }}>Uppfyller alla krav</span>
              ) : (
                <span style={{ background: 'rgba(192,57,43,0.15)', color: '#c0392b', fontSize: 12, padding: '4px 12px', borderRadius: 10 }}>Uppfyller ej alla krav</span>
              )}
            </div>

            {/* Contract status */}
            {selected.contract && (
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 5, padding: 14, marginBottom: 16 }}>
                <div style={{ color: T.amber, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, marginBottom: 8 }}>Kontrakt</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                  <div><span style={{ color: T.textMuted }}>Status: </span><span style={{ color: T.text }}>{selected.contract.status === 'signed' ? 'Signerat' : selected.contract.status === 'pending_signature' ? 'Vantar signatur' : selected.contract.status}</span></div>
                  <div><span style={{ color: T.textMuted }}>Belopp: </span><span style={{ color: T.text }}>{selected.contract.amount?.toLocaleString('sv-SE')} kr</span></div>
                  <div><span style={{ color: T.textMuted }}>Betalning: </span><span style={{ color: selected.contract.paymentStatus === 'paid' ? '#61D836' : T.amber }}>{selected.contract.paymentStatus === 'paid' ? 'Betald' : 'Ej betald'}</span></div>
                  {selected.contract.signatureName && <div><span style={{ color: T.textMuted }}>Signerad av: </span><span style={{ color: T.text }}>{selected.contract.signatureName}</span></div>}
                </div>
              </div>
            )}

            {/* Actions */}
            {(selected.status === 'pending' || selected.status === 'dispensation_granted') && (
              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button onClick={async () => { await approve(selected.id); setSelected(null); }} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 5, padding: '12px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer', flex: 1 }}>Godkann</button>
                  <button onClick={async () => { const n = prompt('Orsak (valfri):') || undefined; await reject(selected.id, n); setSelected(null); }} style={{ background: 'rgba(192,57,43,0.2)', color: '#c0392b', border: 'none', borderRadius: 5, padding: '12px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer', flex: 1 }}>Avvisa</button>
                </div>

                {/* Ask question */}
                <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 5, padding: 14, marginBottom: 12 }}>
                  <div style={{ color: '#00A2FF', fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Stall en fraga</div>
                  <input value={questionText} onChange={e => setQuestionText(e.target.value)} placeholder="Skriv en fraga till den sokande..." style={{ ...iS, marginBottom: 8 }} />
                  <button onClick={() => askQuestion(selected.id)} disabled={questionBusy || !questionText.trim()} style={{ background: '#00A2FF', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 16px', fontSize: 12, cursor: 'pointer', width: '100%' }}>{questionBusy ? 'Skickar...' : 'Skicka fraga'}</button>
                </div>

                {/* Dispensation */}
                {!selected.meetsRequirements && (
                  <div style={{ background: 'rgba(155,89,182,0.08)', border: '1px solid rgba(155,89,182,0.25)', borderRadius: 5, padding: 14 }}>
                    <div style={{ color: '#9b59b6', fontSize: 12, fontWeight: 700, marginBottom: 10 }}>Begar dispens</div>
                    <input value={dispensationTarget} onChange={e => setDispensationTarget(e.target.value)} placeholder="E-post till markagare/jaktrattinnehavare" style={{ ...iS, marginBottom: 8 }} />
                    <input value={dispensationMsg} onChange={e => setDispensationMsg(e.target.value)} placeholder="Meddelande (valfritt)" style={{ ...iS, marginBottom: 8 }} />
                    <button onClick={() => askDispensation(selected)} disabled={dispensationBusy} style={{ background: '#9b59b6', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 16px', fontSize: 12, cursor: 'pointer', width: '100%' }}>{dispensationBusy ? 'Skickar...' : 'Begar dispens'}</button>
                  </div>
                )}
              </div>
            )}

            {/* Send contract (after approval) */}
            {selected.status === 'approved' && !selected.contract && (
              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, position: 'relative', zIndex: 10 }}>
                {!showContractForm && !showContractBuilder ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onPointerDown={(e) => { e.stopPropagation(); setShowContractBuilder(true); }} onClick={(e) => { e.stopPropagation(); setShowContractBuilder(true); }} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 5, padding: '16px 24px', fontSize: 16, fontWeight: 700, cursor: 'pointer', flex: 2, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', userSelect: 'none' }}>Skapa kontrakt</button>
                    <button onPointerDown={(e) => { e.stopPropagation(); gql(`query M($id: ID!) { mark(id: $id) { swishNumber swishAmount contractTemplate } }`, { id: markId }).then(d => { if (d.mark) { setContractSwish(d.mark.swishNumber || ''); setContractAmount(d.mark.swishAmount || 0); setContractText(d.mark.contractTemplate || ''); } }).catch(() => {}); setShowContractForm(true); }} onClick={(e) => { e.stopPropagation(); setShowContractForm(true); }} style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 5, padding: '16px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', flex: 1, touchAction: 'manipulation' }}>Fritext</button>
                  </div>
                ) : showContractBuilder ? (
                  <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 5, padding: 16, maxHeight: '70vh', overflowY: 'auto' }}>
                    <ContractBuilder
                      markId={markId}
                      onGenerate={(text) => {
                        setContractText(text);
                        setShowContractBuilder(false);
                        setShowContractForm(true);
                        // Pre-load swish info
                        gql(`query M($id: ID!) { mark(id: $id) { swishNumber swishAmount } }`, { id: markId })
                          .then(d => { if (d.mark) { setContractSwish(d.mark.swishNumber || ''); setContractAmount(d.mark.swishAmount || 0); } })
                          .catch(() => {});
                      }}
                      onCancel={() => setShowContractBuilder(false)}
                    />
                  </div>
                ) : (
                  <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 5, padding: 16 }}>
                    <div style={{ color: T.amber, fontSize: 12, fontWeight: 700, marginBottom: 12 }}>Kontraktstext och betalning</div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={lS}>Kontraktstext</label>
                      <textarea value={contractText} onChange={e => setContractText(e.target.value)} placeholder="Ange villkor for medlemskap/deltagande..." style={{ ...iS, minHeight: 180, resize: 'vertical' as const }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <div>
                        <label style={lS}>Belopp (kr)</label>
                        <input type="number" value={contractAmount} onChange={e => setContractAmount(Number(e.target.value))} style={iS} />
                      </div>
                      <div>
                        <label style={lS}>Swish-nummer för utbetalning</label>
                        <input value={contractSwish} onChange={e => setContractSwish(e.target.value)} placeholder="123 456 78 90" style={iS} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => { setShowContractForm(false); setContractText(''); }} style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '10px 16px', cursor: 'pointer', flex: 1 }}>Avbryt</button>
                      <button onClick={() => sendContract(selected.id)} disabled={contractBusy || !contractText.trim() || contractAmount <= 0} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 4, padding: '10px 16px', fontWeight: 700, cursor: 'pointer', flex: 2 }}>{contractBusy ? 'Skickar...' : 'Skicka kontrakt'}</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Dispensation status */}
            {selected.dispensationRequest && (
              <div style={{ background: 'rgba(155,89,182,0.1)', border: '1px solid rgba(155,89,182,0.3)', borderRadius: 5, padding: 14, marginTop: 12 }}>
                <div style={{ color: '#9b59b6', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Dispensstatus: {selected.dispensationRequest.status}</div>
                {selected.dispensationRequest.decisionMessage && <div style={{ color: T.textSub, fontSize: 12 }}>{selected.dispensationRequest.decisionMessage}</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Requirements editor */}
      {showReqEditor && <RequirementsEditor markId={markId} existing={requirements} onClose={() => { setShowReqEditor(false); load(); }} />}

    </div>
  );
}

// ── SWISH SETTINGS EDITOR ──────────────────────────────────────
function SwishSettingsEditor({ markId, onClose }: { markId: string; onClose: () => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [swishNumber, setSwishNumber] = useState('');
  const [swishAmount, setSwishAmount] = useState(0);
  const [contractTemplate, setContractTemplate] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Load current mark settings
    gql(`query M($id: ID!) { mark(id: $id) { swishNumber swishAmount contractTemplate } }`, { id: markId })
      .then(d => {
        if (d.mark) {
          setSwishNumber(d.mark.swishNumber || '');
          setSwishAmount(d.mark.swishAmount || 0);
          setContractTemplate(d.mark.contractTemplate || '');
        }
      }).catch(() => {});
  }, [markId]);

  async function save() {
    setBusy(true);
    try {
      await gql(`mutation UMS($markId: ID!, $swish: String!, $amount: Float!, $template: String) { updateMarkSwish(markId: $markId, swishNumber: $swish, swishAmount: $amount, contractTemplate: $template) }`, { markId, swish: swishNumber, amount: swishAmount, template: contractTemplate || undefined });
      onClose();
    } catch (e: any) { alert(e.message); }
    setBusy(false);
  }

  const iS: React.CSSProperties = { width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '10px 14px', color: T.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const };
  const lS: React.CSSProperties = { display: 'block', color: T.textSub, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, marginBottom: 6 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 28, width: '100%', maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ color: T.text, margin: 0, fontSize: 18, fontWeight: 800 }}>Utbetalning & kontraktsinställningar</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 20 }}>x</button>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={lS}>Swish-nummer för utbetalning</label>
          <input value={swishNumber} onChange={e => setSwishNumber(e.target.value)} placeholder="123 456 78 90" style={iS} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={lS}>Medlemsavgift (kr)</label>
          <input type="number" value={swishAmount} onChange={e => setSwishAmount(Number(e.target.value))} style={iS} />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={lS}>Kontraktsmall (standard-text)</label>
          <textarea value={contractTemplate} onChange={e => setContractTemplate(e.target.value)} placeholder="Standardvillkor som föreslås vid varje nytt kontrakt..." style={{ ...iS, minHeight: 100, resize: 'vertical' as const }} />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={onClose} style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 5, padding: '12px 20px', cursor: 'pointer', flex: 1 }}>Avbryt</button>
          <button onClick={save} disabled={busy} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 5, padding: '12px 20px', fontWeight: 700, cursor: 'pointer', flex: 2 }}>{busy ? 'Sparar...' : 'Spara'}</button>
        </div>
      </div>
    </div>
  );
}

// ── OWNER SIGNING PANEL ─────────────────────────────────────────
export function OwnerSigningPanel({ markId }: { markId: string }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [apps, setApps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState<{ appId: string; contractId: string; name: string } | null>(null);
  const [sigName, setSigName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    gql(`query POS($markId: ID!) { pendingOwnerSignatures(markId: $markId) {
      id applicantName applicantEmail interestRoles status updatedAt
      contract { id contractText amount swishNumber signedAt signatureName }
    } }`, { markId })
      .then(d => setApps(d.pendingOwnerSignatures || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [markId]);

  const doSign = async () => {
    if (!sigName.trim() || !signing) return;
    setBusy(true); setError('');
    try {
      await gql(`mutation OS($id: ID!, $name: String!) { ownerSignContract(contractId: $id, signatureName: $name) { id status } }`,
        { id: signing.contractId, name: sigName });
      setSigning(null); setSigName('');
      load();
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };

  if (loading) return <div style={{ color: T.textSub, fontSize: 13, padding: 8 }}>Laddar...</div>;
  if (!apps.length) return (
    <div style={{ color: T.textSub, fontSize: 13, padding: '12px 0', textAlign: 'center' }}>
      Inga avtal väntar på din signatur
    </div>
  );

  return (
    <div>
      <div style={{ color: T.text, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
        Väntar på din signatur — {apps.length} {apps.length === 1 ? 'person' : 'personer'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {apps.map(app => (
          <div key={app.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div>
                <div style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>{app.applicantName || app.applicantEmail}</div>
                <div style={{ color: T.textSub, fontSize: 12 }}>{app.applicantEmail}</div>
              </div>
              <div style={{ background: 'rgba(155,89,182,0.15)', border: '1px solid rgba(155,89,182,0.4)', borderRadius: 4, padding: '3px 8px', fontSize: 11, color: '#9b59b6', fontWeight: 600 }}>
                Väntar på din signatur
              </div>
            </div>
            {app.interestRoles?.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                {app.interestRoles.map((r: string) => (
                  <span key={r} style={{ background: `${T.amber}20`, color: T.amber, border: `1px solid ${T.amber}40`, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                    {INTEREST_ROLES.find(x => x.id === r)?.label || r}
                  </span>
                ))}
              </div>
            )}
            {app.contract?.signedAt && (
              <div style={{ color: T.textSub, fontSize: 11, marginBottom: 8 }}>
                Signerat av {app.contract.signatureName} • {new Date(app.contract.signedAt).toLocaleDateString('sv-SE')}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => { setSigning({ appId: app.id, contractId: app.contract.id, name: app.applicantName }); setSigName(''); setError(''); }}
                style={{ background: '#61D836', color: '#000', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Signera & godkänn
              </button>
            </div>
          </div>
        ))}
      </div>

      {signing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: T.bg, borderRadius: 12, padding: 28, maxWidth: 460, width: '100%' }}>
            <div style={{ color: T.text, fontSize: 17, fontWeight: 700, marginBottom: 6 }}>Signera & godkänn</div>
            <div style={{ color: T.textSub, fontSize: 13, marginBottom: 16 }}>
              Godkänner {signing.name} som kvalificerad på denna mark
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Ditt namn (elektronisk signatur)</div>
              <input value={sigName} onChange={e => setSigName(e.target.value)}
                placeholder="Skriv ditt fullständiga namn"
                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            {error && <div style={{ color: '#e55', fontSize: 13, marginBottom: 10 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setSigning(null)} style={{ flex: 1, background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: '11px', fontSize: 13, cursor: 'pointer' }}>
                Avbryt
              </button>
              <button onClick={doSign} disabled={busy || !sigName.trim()}
                style={{ flex: 2, background: '#61D836', color: '#000', border: 'none', borderRadius: 6, padding: '11px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: busy || !sigName.trim() ? 0.6 : 1 }}>
                {busy ? 'Signerar...' : 'Bekräfta — Kvalificera person'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── REQUIREMENTS EDITOR ────────────────────────────────────────
export function RequirementsEditor({ markId, existing, onClose }: { markId: string; existing: MarkRequirements | null; onClose: () => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [form, setForm] = useState({
    minExperienceYears: existing?.minExperienceYears || 0,
    requiresWeaponLicense: existing?.requiresWeaponLicense || false,
    requiresDog: existing?.requiresDog || false,
    minHunterYears: existing?.minHunterYears || 0,
    afterhuntRequired: existing?.afterhuntRequired || false,
    customRequirements: existing?.customRequirements || [] as Array<{ label: string; required: boolean }>,
    requirementsVisible: existing?.requirementsVisible !== false,
    delegatedReviewerRoles: existing?.delegatedReviewerRoles || ['jaktledare', 'admin'],
  });
  const [busy, setBusy] = useState(false);
  const [newCustom, setNewCustom] = useState('');

  const iS: React.CSSProperties = { width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '10px 14px', color: T.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'inherit' };
  const lS: React.CSSProperties = { display: 'block', color: T.textSub, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, marginBottom: 6 };

  async function save() {
    setBusy(true);
    try {
      await gql(`mutation SMR($markId: ID!, $input: MarkRequirementsInput!) { setMarkRequirements(markId: $markId, input: $input) { id } }`, { markId, input: form });
      onClose();
    } catch (e: any) { alert(e.message); }
    setBusy(false);
  }

  const allRoles = ['markagare', 'jaktrattinnehavare', 'jaktledare', 'admin', 'vice jaktledare'];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ color: T.text, margin: 0, fontSize: 18, fontWeight: 800 }}>Kravprofil</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 20 }}>x</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 16 }}>
          <div>
            <label style={lS}>Minimum jagarerfarenhet (ar)</label>
            <input type="number" min={0} max={50} value={form.minExperienceYears} onChange={e => setForm(f => ({ ...f, minExperienceYears: Number(e.target.value) }))} style={{ ...iS, width: 100 }} />
          </div>

          {[
            { k: 'requiresWeaponLicense', l: 'Vapenlicens kravs' },
            { k: 'requiresDog', l: 'Jakthund kravs' },
            { k: 'afterhuntRequired', l: 'Eftersoksutbildning kravs' },
          ].map(({ k, l }) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: T.text, fontSize: 13 }}>
              <input type="checkbox" checked={form[k as keyof typeof form] as boolean} onChange={e => setForm(f => ({ ...f, [k]: e.target.checked }))} style={{ accentColor: T.amber, width: 16, height: 16 }} />
              {l}
            </label>
          ))}

          <div>
            <label style={lS}>Egna krav</label>
            <div style={{ marginBottom: 8 }}>
              {form.customRequirements.map((cr, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ color: T.text, fontSize: 13, flex: 1 }}>{cr.label}</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, color: T.textSub, fontSize: 11 }}>
                    <input type="checkbox" checked={cr.required} onChange={e => setForm(f => ({ ...f, customRequirements: f.customRequirements.map((r, j) => j === i ? { ...r, required: e.target.checked } : r) }))} style={{ accentColor: T.amber }} />
                    Obligatorisk
                  </label>
                  <button onClick={() => setForm(f => ({ ...f, customRequirements: f.customRequirements.filter((_, j) => j !== i) }))} style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: 16 }}>x</button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={newCustom} onChange={e => setNewCustom(e.target.value)} placeholder="Nytt krav..." style={{ ...iS, flex: 1 }} onKeyDown={e => { if (e.key === 'Enter' && newCustom.trim()) { setForm(f => ({ ...f, customRequirements: [...f.customRequirements, { label: newCustom.trim(), required: true }] })); setNewCustom(''); } }} />
              <button onClick={() => { if (newCustom.trim()) { setForm(f => ({ ...f, customRequirements: [...f.customRequirements, { label: newCustom.trim(), required: true }] })); setNewCustom(''); } }} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 4, padding: '10px 16px', fontWeight: 700, cursor: 'pointer' }}>+</button>
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: T.text, fontSize: 13 }}>
            <input type="checkbox" checked={form.requirementsVisible} onChange={e => setForm(f => ({ ...f, requirementsVisible: e.target.checked }))} style={{ accentColor: T.amber, width: 16, height: 16 }} />
            Visa krav for sokande
          </label>

          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button onClick={onClose} style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 5, padding: '12px 20px', cursor: 'pointer', flex: 1 }}>Avbryt</button>
            <button onClick={save} disabled={busy} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 5, padding: '12px 20px', fontWeight: 700, cursor: 'pointer', flex: 2 }}>{busy ? 'Sparar...' : 'Spara kravprofil'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MY CONTRACTS (for user profile) ────────────────────────────
export function MyContracts() {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [contracts, setContracts] = useState<ApplicationContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState<ApplicationContract | null>(null);

  useEffect(() => {
    gql(`query MC { myContracts { id applicationId markId applicantEmail contractType contractText amount swishNumber status signedAt signatureName paymentStatus paymentConfirmedAt createdAt } }`)
      .then(d => setContracts(d.myContracts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: T.textSub, fontSize: 13, padding: 20 }}>Laddar kontrakt...</div>;
  if (contracts.length === 0) return null;

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ color: T.amber, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Mina kontrakt</div>
      {contracts.map(c => {
        const isPaid = c.paymentStatus === 'paid';
        const isSigned = c.status === 'signed';
        return (
          <div key={c.id} onClick={() => setSelectedContract(c)} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 5, padding: 14, marginBottom: 8, cursor: 'pointer', transition: 'all 0.2s' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ color: T.text, fontSize: 13, fontWeight: 700 }}>{c.contractType === 'membership' ? 'Medlemskontrakt' : c.contractType}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <span style={{ background: isSigned ? '#61D83622' : `${T.amber}22`, color: isSigned ? '#61D836' : T.amber, fontSize: 10, padding: '2px 8px', borderRadius: 10 }}>{isSigned ? 'Signerat' : 'Ej signerat'}</span>
                <span style={{ background: isPaid ? '#61D83622' : 'rgba(192,57,43,0.15)', color: isPaid ? '#61D836' : '#c0392b', fontSize: 10, padding: '2px 8px', borderRadius: 10 }}>{isPaid ? 'Betald' : 'Ej betald'}</span>
              </div>
            </div>
            <div style={{ color: T.textSub, fontSize: 12 }}>{c.amount.toLocaleString('sv-SE')} kr — {new Date(c.createdAt).toLocaleDateString('sv-SE')}</div>
            {c.signatureName && <div style={{ color: T.textMuted, fontSize: 11, marginTop: 4 }}>Signerad av: {c.signatureName}</div>}
          </div>
        );
      })}

      {/* Contract detail modal */}
      {selectedContract && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={e => e.target === e.currentTarget && setSelectedContract(null)}>
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '20px 24px', width: '100%', maxWidth: 680, maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: T.text, margin: 0, fontSize: 16, fontWeight: 700 }}>Avtalshandling</h3>
              <button onClick={() => setSelectedContract(null)} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 22 }}>×</button>
            </div>

            <LegalContractDocument
              contractText={selectedContract.contractText}
              amount={selectedContract.amount}
              signatureName={selectedContract.signatureName || undefined}
              signedAt={selectedContract.signedAt || undefined}
              applicantEmail={selectedContract.applicantEmail}
              contractRef={`QV-${selectedContract.id.slice(-6).toUpperCase()}`}
              createdAt={selectedContract.createdAt}
              showSignaturePlaceholder={false}
            />

            {selectedContract.status !== 'signed' && (
              <div style={{ color: T.textSub, fontSize: 12, padding: '12px 16px', background: T.surface, borderRadius: 5, marginTop: 12, textAlign: 'center' }}>Inväntar signering...</div>
            )}
            <button onClick={() => setSelectedContract(null)} style={{ width: '100%', background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 5, padding: '12px 20px', cursor: 'pointer', marginTop: 16 }}>Stäng</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CONTRACT TAB (templates + signed contracts for mark detail) ──
interface ContractTemplateItem {
  id: string;
  markId: string;
  name: string;
  contractText: string;
  amount: number;
  swishNumber: string | null;
  isDefault: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export function ContractTab({ markId, isAdmin }: { markId: string; isAdmin: boolean }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [templates, setTemplates] = useState<ContractTemplateItem[]>([]);
  const [signedContracts, setSignedContracts] = useState<ApplicationContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editTemplate, setEditTemplate] = useState<ContractTemplateItem | null>(null);
  const [editName, setEditName] = useState('');
  const [editText, setEditText] = useState('');
  const [editAmount, setEditAmount] = useState(0);
  const [editSwish, setEditSwish] = useState('');
  const [editDefault, setEditDefault] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [viewContract, setViewContract] = useState<ApplicationContract | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateAmount, setTemplateAmount] = useState(0);
  const [templateSwish, setTemplateSwish] = useState('');
  const [templateDefault, setTemplateDefault] = useState(false);
  const [pendingText, setPendingText] = useState('');

  async function load() {
    setLoading(true);
    try {
      const [tpl, ctr] = await Promise.all([
        gql(`query CT($id: ID!) { contractTemplates(markId: $id) { id markId name contractText amount swishNumber isDefault createdBy createdAt updatedAt } }`, { id: markId }),
        gql(`query MC($id: ID!) { markContracts(markId: $id) { id applicationId markId applicantEmail contractType contractText amount swishNumber status signedAt signatureName paymentStatus paymentConfirmedAt paymentReference createdAt updatedAt } }`, { id: markId }),
      ]);
      setTemplates(tpl.contractTemplates || []);
      setSignedContracts(ctr.markContracts || []);
    } catch { /* ignore */ }
    setLoading(false);
  }

  useEffect(() => { load(); }, [markId]);

  function openEdit(tpl: ContractTemplateItem) {
    setEditTemplate(tpl);
    setEditName(tpl.name);
    setEditText(tpl.contractText);
    setEditAmount(tpl.amount);
    setEditSwish(tpl.swishNumber || '');
    setEditDefault(tpl.isDefault);
  }

  async function saveEdit() {
    if (!editTemplate) return;
    setSaveBusy(true);
    try {
      await gql(`mutation UT($id: ID!, $name: String, $text: String, $amount: Float, $swish: String, $def: Boolean) { updateContractTemplate(id: $id, name: $name, contractText: $text, amount: $amount, swishNumber: $swish, isDefault: $def) { id } }`, { id: editTemplate.id, name: editName, text: editText, amount: editAmount, swish: editSwish || null, def: editDefault });
      setEditTemplate(null);
      await load();
    } catch (e: any) { alert(e.message); }
    setSaveBusy(false);
  }

  async function deleteTemplate(id: string) {
    if (!confirm('Ta bort denna kontraktsmall?')) return;
    try {
      await gql(`mutation DT($id: ID!) { deleteContractTemplate(id: $id) }`, { id });
      await load();
    } catch (e: any) { alert(e.message); }
  }

  async function saveNewTemplate(contractText: string) {
    setPendingText(contractText);
    setShowBuilder(false);
  }

  async function createTemplate() {
    if (!templateName.trim() || !pendingText.trim()) return;
    setSaveBusy(true);
    try {
      await gql(`mutation CCT($markId: ID!, $name: String!, $text: String!, $amount: Float!, $swish: String, $def: Boolean) { createContractTemplate(markId: $markId, name: $name, contractText: $text, amount: $amount, swishNumber: $swish, isDefault: $def) { id } }`, { markId, name: templateName, text: pendingText, amount: templateAmount, swish: templateSwish || null, def: templateDefault });
      setTemplateName('');
      setPendingText('');
      setTemplateAmount(0);
      setTemplateSwish('');
      setTemplateDefault(false);
      await load();
    } catch (e: any) { alert(e.message); }
    setSaveBusy(false);
  }

  const iS: React.CSSProperties = { width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '10px 14px', color: T.text, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const lS: React.CSSProperties = { display: 'block', color: T.textSub, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, marginBottom: 6 };

  if (loading) return <div style={{ color: T.textSub, fontSize: 13, padding: 40, textAlign: 'center' }}>Laddar kontrakt...</div>;

  return (
    <div style={{ padding: '0 clamp(10px,3vw,28px) 32px' }}>

      {/* ── TEMPLATES SECTION (admin only) ── */}
      {isAdmin && (
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ color: T.amber, fontSize: 16, fontWeight: 800 }}>Kontraktsmallar</div>
            {!showBuilder && !pendingText && (
              <button onClick={() => setShowBuilder(true)} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 5, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Skapa kontraktsmall</button>
            )}
          </div>

          {/* Contract builder inline */}
          {showBuilder && (
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20, marginBottom: 20, maxHeight: '70vh', overflowY: 'auto' }}>
              <ContractBuilder markId={markId} onGenerate={saveNewTemplate} onCancel={() => setShowBuilder(false)} />
            </div>
          )}

          {/* Save template form (after builder generates text) */}
          {pendingText && !showBuilder && (
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20, marginBottom: 20 }}>
              <div style={{ color: T.amber, fontSize: 13, fontWeight: 700, marginBottom: 16 }}>Spara som kontraktsmall</div>
              <div style={{ marginBottom: 12 }}>
                <label style={lS}>Mallnamn</label>
                <input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="T.ex. Standardavtal 2026/2027" style={iS} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={lS}>Förhandsgranskning</label>
                <div style={{ maxHeight: 320, overflowY: 'auto', borderRadius: 4, border: `1px solid ${T.border}` }}>
                  <LegalContractDocument
                    contractText={pendingText}
                    markName="[Jaktmarkens namn]"
                    contractRef="QV-MALL"
                    showSignaturePlaceholder={true}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={lS}>Belopp (kr)</label>
                  <input type="number" value={templateAmount} onChange={e => setTemplateAmount(Number(e.target.value))} style={iS} />
                </div>
                <div>
                  <label style={lS}>Swish-nummer för utbetalning</label>
                  <input value={templateSwish} onChange={e => setTemplateSwish(e.target.value)} placeholder="123 456 78 90" style={iS} />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.text, fontSize: 13, marginBottom: 16, cursor: 'pointer' }}>
                <input type="checkbox" checked={templateDefault} onChange={e => setTemplateDefault(e.target.checked)} style={{ accentColor: T.amber }} />
                Standardmall (används automatiskt vid nya kontrakt)
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setPendingText(''); setTemplateName(''); }} style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '10px 16px', cursor: 'pointer', flex: 1 }}>Avbryt</button>
                <button onClick={createTemplate} disabled={saveBusy || !templateName.trim()} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 4, padding: '10px 16px', fontWeight: 700, cursor: 'pointer', flex: 2 }}>{saveBusy ? 'Sparar...' : 'Spara mall'}</button>
              </div>
            </div>
          )}

          {/* Template list */}
          {templates.length === 0 && !showBuilder && !pendingText ? (
            <div style={{ color: T.textMuted, fontSize: 13, textAlign: 'center', padding: 24, background: T.surface, borderRadius: 6, border: `1px solid ${T.border}` }}>Inga kontraktsmallar skapade. Klicka "Skapa kontraktsmall" för att börja.</div>
          ) : (
            templates.map(tpl => (
              <div key={tpl.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 16, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>{tpl.name}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      {tpl.isDefault && <span style={{ background: `${T.amber}22`, color: T.amber, fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>Standard</span>}
                      {tpl.amount > 0 && <span style={{ background: `${T.amber}15`, color: T.textSub, fontSize: 10, padding: '2px 8px', borderRadius: 10 }}>{Math.round(tpl.amount).toLocaleString('sv-SE')} kr</span>}
                      {tpl.swishNumber && <span style={{ background: 'rgba(76,175,80,0.15)', color: '#4CAF50', fontSize: 10, padding: '2px 8px', borderRadius: 10 }}>Swish</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => openEdit(tpl)} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 4, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Redigera</button>
                    <button onClick={() => deleteTemplate(tpl.id)} style={{ background: 'rgba(192,57,43,0.15)', color: '#c0392b', border: 'none', borderRadius: 4, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}>Ta bort</button>
                  </div>
                </div>
                <div style={{ color: T.textMuted, fontSize: 12, lineHeight: 1.6, maxHeight: 80, overflow: 'hidden', whiteSpace: 'pre-wrap' }}>{tpl.contractText.slice(0, 250)}{tpl.contractText.length > 250 ? '...' : ''}</div>
                <div style={{ color: T.textMuted, fontSize: 10, marginTop: 6 }}>Skapad {new Date(tpl.createdAt).toLocaleDateString('sv-SE')}</div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── SIGNED CONTRACTS SECTION ── */}
      <div>
        <div style={{ color: T.amber, fontSize: 16, fontWeight: 800, marginBottom: 16 }}>Signerade kontrakt</div>
        {signedContracts.length === 0 ? (
          <div style={{ color: T.textMuted, fontSize: 13, textAlign: 'center', padding: 24, background: T.surface, borderRadius: 6, border: `1px solid ${T.border}` }}>Inga kontrakt har signerats än.</div>
        ) : (
          signedContracts.map(c => {
            const isSigned = c.status === 'signed';
            const isPaid = c.paymentStatus === 'paid';
            return (
              <div key={c.id} onClick={() => setViewContract(c)} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 16, marginBottom: 10, cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>{c.applicantEmail}</div>
                    <div style={{ color: T.textSub, fontSize: 12, marginTop: 2 }}>{c.contractType === 'membership' ? 'Medlemskontrakt' : c.contractType}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <span style={{ background: isSigned ? '#61D83622' : `${T.amber}22`, color: isSigned ? '#61D836' : T.amber, fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{isSigned ? 'Signerat' : 'Ej signerat'}</span>
                    <span style={{ background: isPaid ? '#61D83622' : 'rgba(192,57,43,0.15)', color: isPaid ? '#61D836' : '#c0392b', fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{isPaid ? 'Betald' : 'Ej betald'}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: T.textMuted }}>
                  <span>{Math.round(c.amount).toLocaleString('sv-SE')} kr</span>
                  {c.signatureName && <span>Signerad av: {c.signatureName}</span>}
                  <span>{new Date(c.createdAt).toLocaleDateString('sv-SE')}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── VIEW CONTRACT DETAIL MODAL ── */}
      {viewContract && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={e => e.target === e.currentTarget && setViewContract(null)}>
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '20px 24px', width: '100%', maxWidth: 680, maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: T.text, margin: 0, fontSize: 16, fontWeight: 700 }}>Avtalshandling — {viewContract.applicantEmail}</h3>
              <button onClick={() => setViewContract(null)} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 22 }}>×</button>
            </div>

            {(viewContract.contractType === 'hunt' || viewContract.contractType === 'hunt_package') ? (
              <GastjaktsAvtal
                huntName={(viewContract as any).huntName || undefined}
                huntDates={(viewContract as any).huntDates || undefined}
                numHunts={(viewContract as any).numHunts || 1}
                isPackage={viewContract.contractType === 'hunt_package'}
                amount={Math.round(viewContract.amount)}
                pricePerHunt={(viewContract as any).pricePerHunt || undefined}
                signatureName={viewContract.signatureName || undefined}
                signedAt={viewContract.signedAt || undefined}
                applicantEmail={viewContract.applicantEmail}
                contractRef={`QV-GJ-${viewContract.id.slice(-6).toUpperCase()}`}
                createdAt={viewContract.createdAt}
                showSignaturePlaceholder={!viewContract.signatureName}
              />
            ) : (
              <LegalContractDocument
                contractText={viewContract.contractText}
                amount={Math.round(viewContract.amount)}
                signatureName={viewContract.signatureName || undefined}
                signedAt={viewContract.signedAt || undefined}
                applicantEmail={viewContract.applicantEmail}
                contractRef={`QV-${viewContract.id.slice(-6).toUpperCase()}`}
                createdAt={viewContract.createdAt}
                showSignaturePlaceholder={!viewContract.signatureName}
              />
            )}

            {viewContract.paymentConfirmedAt && (
              <div style={{ background: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.3)', borderRadius: 5, padding: 12, marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#4CAF50', fontSize: 13, fontWeight: 700 }}>✓ Betalning bekräftad</span>
                <span style={{ color: T.textSub, fontSize: 12 }}>{new Date(viewContract.paymentConfirmedAt).toLocaleDateString('sv-SE')}</span>
              </div>
            )}

            <button onClick={() => setViewContract(null)} style={{ width: '100%', background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 5, padding: '12px 20px', cursor: 'pointer', marginTop: 16 }}>Stäng</button>
          </div>
        </div>
      )}

      {/* ── EDIT TEMPLATE MODAL ── */}
      {editTemplate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={e => e.target === e.currentTarget && setEditTemplate(null)}>
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 28, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ color: T.text, margin: 0, fontSize: 18 }}>Redigera kontraktsmall</h3>
              <button onClick={() => setEditTemplate(null)} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 20 }}>x</button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={lS}>Mallnamn</label>
              <input value={editName} onChange={e => setEditName(e.target.value)} style={iS} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={lS}>Kontraktstext</label>
              <textarea value={editText} onChange={e => setEditText(e.target.value)} style={{ ...iS, minHeight: 250, resize: 'vertical' as const }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={lS}>Belopp (kr)</label>
                <input type="number" value={editAmount} onChange={e => setEditAmount(Number(e.target.value))} style={iS} />
              </div>
              <div>
                <label style={lS}>Swish-nummer för utbetalning</label>
                <input value={editSwish} onChange={e => setEditSwish(e.target.value)} placeholder="123 456 78 90" style={iS} />
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.text, fontSize: 13, marginBottom: 16, cursor: 'pointer' }}>
              <input type="checkbox" checked={editDefault} onChange={e => setEditDefault(e.target.checked)} style={{ accentColor: T.amber }} />
              Standardmall
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setEditTemplate(null)} style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '10px 16px', cursor: 'pointer', flex: 1 }}>Avbryt</button>
              <button onClick={saveEdit} disabled={saveBusy || !editName.trim()} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 4, padding: '10px 16px', fontWeight: 700, cursor: 'pointer', flex: 2 }}>{saveBusy ? 'Sparar...' : 'Spara'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
